// POLICYINTEL_V4_MAIN_VALUE_PREVIEW_20261001
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppSelector } from '../store/hooks';
import { getLiveSummary, getLiveSummaryChanges, getLiveSummaryThemeStocks, type LiveSummaryApiResponse, type LiveSummaryChangeItem, type LiveSummaryChangesResponse, type LiveSummaryTab, type LiveSummaryThemeStock } from '../api/liveSummaryApi';
import { saveStockTrackPending } from '../shared/stockTrackPending';
import P1ShadowEvidenceCard from '../components/live/P1ShadowEvidenceCard';

import {
  getScannerDashboard,
  setScannerRuleEnabled,
} from '../api/scannerApi';
import { getMarketIntraday, type MarketIntradayResponse } from '../api/marketIntraday';
import {
  getOffhoursMarketProxy,
  type OffhoursProxyResponse,
} from '../api/offhoursMarketProxyApi';
import {
  getOffhoursMarketProxyHistory,
  type OffhoursProxyHistoryResponse,
} from '../api/offhoursMarketProxyHistoryApi';
import '../components/live/offhoursMarketProxy.css';
import { trackEvent, trackPageView } from '../shared/analytics/ga4';
import { trackUxEvent } from '../analytics/analytics';
import { emitTrackedReturnMilestones } from '../analytics/retentionMilestones';

import {
  normalizeActionDecision,
  normalizeRiskAssessment,
  riskAssessmentPriority,
  userFacingEntryStage,
  type NormalizedActionDecision,
  type NormalizedRiskAssessment,
} from '../components/live/liveSummaryRiskAssessment';
import SEO from '../components/SEO';
import UserFeedbackLauncher from '../components/feedback/UserFeedbackLauncher';
import { searchStockSuggestions, type StockSearchSuggestion } from '../api/stockSearchApi';
import './policyIntelConversionRefresh.css';

// POLICYINTEL_V8_SIGNAL_WORKSPACE (single visible MainPage; shared App.tsx header)
const PI7_EMBEDDED_STYLES = `
.pi2-page{--ink:#122033;--muted:#607086;--line:#E2E8F0;--blue:#2F6BFF;--navy:#10263F;--bg:#F6F8FB;--risk:#D84C57;--stable:#178A52;min-height:100vh;background:var(--bg);color:var(--ink);font-family:'Pretendard Variable',Pretendard,'Apple SD Gothic Neo',system-ui,sans-serif;font-size:15px;letter-spacing:-.02em;line-height:1.65}
.pi2-page *, .pi2-page *:before,.pi2-page *:after{box-sizing:border-box}.pi2-page button,.pi2-page input{font:inherit}.pi2-page button{cursor:pointer;transition:background .16s,border-color .16s}.pi2-page button:focus-visible,.pi2-page a:focus-visible,.pi2-page input:focus-visible{outline:3px solid rgba(47,107,255,.38);outline-offset:3px}
.pi2-shell{max-width:1320px;margin:0 auto;padding:34px clamp(18px,3.2vw,45px) 58px;min-width:0}.pi2-eyebrow{font-size:12px;font-weight:850;letter-spacing:.08em;color:#4670B6}.pi2-overline{display:flex;gap:9px;align-items:center;flex-wrap:wrap;color:#718198;font-size:12px;font-weight:650}.pi2-dot{width:7px;height:7px;border-radius:50%;background:#178A52}.pi2-hero{display:grid;grid-template-columns:minmax(0,1.28fr) minmax(280px,.72fr);align-items:stretch;gap:20px}.pi2-primary{padding:clamp(26px,3.5vw,47px);background:#fff;border:1px solid var(--line);border-radius:19px;min-width:0}.pi2-primary h1{font-size:clamp(31px,3vw,44px);line-height:1.24;letter-spacing:-.055em;font-weight:850;word-break:keep-all;margin:15px 0}.pi2-primary p{font-size:15px;color:#607086;max-width:610px;margin:0 0 26px;line-height:1.85}.pi2-primary .pi2-inline-note{font-size:12px;color:#7E8B9B;margin:12px 0 0}.pi2-side{background:var(--navy);color:#fff;padding:clamp(25px,3vw,37px);border-radius:18px;display:flex;flex-direction:column;justify-content:space-between;min-width:0}.pi2-side h2{font-size:21px;line-height:1.42;margin:14px 0 20px;letter-spacing:-.04em}.pi2-side p{color:#CFDBE9;font-size:13px;line-height:1.85}.pi2-side .pi2-eyebrow{color:#90BCFF}.pi2-side small{color:#AEBED1;font-size:12px}.pi2-side-stat{display:flex;justify-content:space-between;align-items:baseline;gap:8px;padding:12px 0;border-top:1px solid rgba(255,255,255,.14);font-size:12px;color:#D7E2F0}.pi2-side-stat strong{font-size:17px;color:#fff;font-weight:800;font-variant-numeric:tabular-nums}.pi2-search{max-width:660px;position:relative}.pi2-search-label{display:block;font-size:12px;font-weight:800;color:#405A79;margin-bottom:9px}.pi2-search-control{display:flex;align-items:stretch;border:1px solid #C8D3E2;border-radius:12px;background:#fff;min-height:59px;padding:5px;box-shadow:0 2px 7px rgba(12,33,56,.035)}.pi2-search-control input{flex:1;min-width:0;border:0;background:none;outline:0;font-size:16px;color:#14243A;padding:10px 13px}.pi2-search-control>button,.pi2-primary-action{border:0;border-radius:9px;background:var(--blue);color:#fff;font-weight:800;padding:12px 23px;white-space:nowrap}.pi2-search-control>button:hover,.pi2-primary-action:hover{background:#2556CB}.pi2-suggestions{z-index:50;position:absolute;top:100%;left:0;right:0;list-style:none;background:#fff;border:1px solid var(--line);border-radius:12px;padding:5px;margin:5px 0;box-shadow:0 16px 36px rgba(16,38,63,.17);max-height:285px;overflow:auto}.pi2-suggestions button{width:100%;background:transparent;border:0;padding:11px 13px;text-align:left;display:flex;justify-content:space-between;gap:10px;border-radius:8px}.pi2-suggestions button:hover{background:#F0F5FF}.pi2-suggestions span{font-size:12px;color:#8190A1}.pi2-section{margin-top:30px;scroll-margin-top:80px}.pi2-sectionhead{display:flex;justify-content:space-between;align-items:center;gap:12px;margin-bottom:13px;flex-wrap:wrap}.pi2-sectionhead h2{font-size:22px;letter-spacing:-.045em;line-height:1.4;margin:0}.pi2-sectionhead p{color:#8290A1;font-size:12px;margin:0}.pi2-result{border:1px solid #D4E1F7;background:#fff;border-radius:17px;padding:clamp(20px,2.7vw,30px);box-shadow:0 5px 24px rgba(34,73,120,.035)}.pi2-result-header{display:flex;justify-content:space-between;gap:14px;align-items:start;flex-wrap:wrap;border-bottom:1px solid var(--line);padding-bottom:19px;margin-bottom:20px}.pi2-result-header h3{margin:5px 0;font-size:27px;letter-spacing:-.05em}.pi2-result-header small{font-size:12px;color:#7D8B9A;font-weight:550}.pi2-price{text-align:right}.pi2-price strong{font-size:24px;font-weight:850;font-variant-numeric:tabular-nums}.pi2-result-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:15px}.pi2-fact{background:#F8FAFD;border:1px solid #EBEFF5;border-radius:12px;padding:18px;min-width:0}.pi2-fact span{display:block;font-size:12px;color:#78889A;font-weight:800;margin-bottom:7px}.pi2-fact strong{display:block;font-size:17px;line-height:1.6;color:#1F344D;word-break:keep-all}.pi2-fact p{font-size:13px;color:#697D91;line-height:1.75;margin:8px 0 0;word-break:keep-all}.pi2-next{background:#EEF4FF;border-color:#D2E2FF}.pi2-next strong{color:#194FA7}.pi2-result-actions{display:flex;gap:9px;flex-wrap:wrap;margin-top:22px}.pi2-secondary-action{background:#fff;border:1px solid #CDD8E6;border-radius:9px;font-size:13px;padding:11px 17px;color:#235AA2;font-weight:800}.pi2-secondary-action:hover{background:#F0F5FF}.pi2-state{padding:18px 22px;background:#fff;border:1px solid var(--line);border-radius:12px;color:#6F8095;font-size:13px}.pi2-state[role=alert]{border-color:#F3BFC9;color:#B33D55;background:#FFF8F8}.pi2-three{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px}.pi2-step{border-top:2px solid #D8E2F0;padding:16px 3px;min-width:0}.pi2-step strong{font-size:15px;display:block;margin:6px 0}.pi2-step p{font-size:13px;line-height:1.75;color:#6A7A90;margin:0}.pi2-watch-list{display:grid;gap:9px}.pi2-watch-row{display:grid;grid-template-columns:110px minmax(0,1fr) auto;gap:12px;align-items:center;text-align:left;border:1px solid var(--line);background:white;padding:15px 18px;border-radius:12px}.pi2-watch-row strong{font-size:15px}.pi2-watch-row small{color:#7B8A9C;font-size:12px;min-width:0}.pi2-watch-row em{color:#2F6BFF;font-style:normal;font-size:12px}.pi2-market{display:grid;grid-template-columns:1fr 1fr;gap:16px}.pi2-marketpanel{padding:23px;border-radius:15px;border:1px solid var(--line);background:white;min-width:0}.pi2-marketpanel h3{font-size:18px;margin:6px 0 14px;letter-spacing:-.03em}.pi2-marketrow{display:flex;justify-content:space-between;gap:10px;align-items:baseline;border-top:1px solid #EDF1F6;padding:10px 0;color:#6A7B91;font-size:13px}.pi2-marketrow strong{font-size:16px;color:#142B43;font-variant-numeric:tabular-nums}.pi2-marketrow em{font-style:normal;font-size:12px}.pi2-page .is-up{color:#178A52}.pi2-page .is-down{color:#D84C57}.pi2-page .is-neutral{color:#77869A}.pi2-footer{padding:23px 2px 0;margin-top:32px;border-top:1px solid var(--line);color:#77869A;display:grid;gap:7px;font-size:12px}.pi2-footer strong{color:#51657B}.pi2-footer button{padding:9px 14px;font-weight:750;border:1px solid #D5DEEB;border-radius:9px;background:white;color:#3B587A;width:max-content}
@media(max-width:1010px){.pi2-hero{grid-template-columns:1fr}.pi2-side{min-height:0}.pi2-market{grid-template-columns:1fr 1fr}}
@media(max-width:720px){.pi2-shell{padding:17px 15px 85px}.pi2-primary{padding:25px 20px}.pi2-primary h1{font-size:31px}.pi2-hero,.pi2-result-grid,.pi2-market,.pi2-three{grid-template-columns:1fr}.pi2-search-control{min-height:56px}.pi2-search-control>button{padding:11px 15px;font-size:13px}.pi2-price{text-align:left}.pi2-watch-row{grid-template-columns:1fr auto}.pi2-watch-row small{grid-column:1/-1;grid-row:2}.pi2-watch-row em{grid-column:2;grid-row:1}.pi2-section{margin-top:25px}.pi2-result-header h3{font-size:24px}}
@media(prefers-reduced-motion:reduce){.pi2-page button{transition:none}}
`;

type AnyRow = Record<string, any>;
type StockSuggestion = StockSearchSuggestion;
type MarketValue = { value?: number | null; changeRate?: number | null; observedAt?: string; source?: string };
type PublicBrief = {
  status?: string;
  sessionDate?: string;
  generatedAt?: string;
  indices?: Record<string, MarketValue>;
  breadth?: { up: number; down: number; observed: number; label?: string; observedAt?: string };
  movers?: Array<{ ticker: string; name: string; priceChangePct: number | null; foreignNetBuyKrw: number | null; foreignIntensityPct: number | null; flowStatus: string }>;
  event?: { headline?: string; observedAt?: string; status?: string; source?: string; url?: string; note?: string } | null;
};
type Props = {
  authenticated: boolean;
  summary: AnyRow | null;
  dashboard?: AnyRow | null;
  changes?: AnyRow[];
  loading?: boolean;
  query: string;
  onQuery: (value: string) => void;
  suggestions: StockSuggestion[];
  onSelect: (value: StockSuggestion) => void;
  onSubmit: () => void;
  preview: AnyRow | null;
  previewLoading: boolean;
  previewError?: string;
  stockResearch?: {
    summary: string;
    change: string;
    checks: string[];
    fundamental: string;
    financialReady: boolean;
    financialBasisDate: string;
    financialMetrics: Array<{label:string;value:string}>;
    disclosureTitle: string;
    disclosureDate: string;
    disclosureImpact: string;
    disclosureEmptyText: string;
  };
  exposureRefs?: {
    price: React.RefObject<HTMLDivElement | null>;
    value: React.RefObject<HTMLDivElement | null>;
    reason: React.RefObject<HTMLDivElement | null>;
    condition: React.RefObject<HTMLDivElement | null>;
    trackCta: React.RefObject<HTMLButtonElement | null>;
  };
  onWatch: (ticker: string, name: string) => void;
  onEvidence: (ticker: string, name: string) => void;
  onOpen: (tab: string, source?: string, target?: { ticker?: string }) => void;
  onNavigate: (url: string) => void;
};

const valueOf = (v: unknown): number | null => {
  if (v === undefined || v === null || v === '') return null;
  const n = Number(v); return Number.isFinite(n) ? n : null;
};
const str = (v: unknown): string => typeof v === 'string' ? v.trim() : v === null || v === undefined ? '' : String(v).trim();
const fmt = (v: number | null, digits = 2): string => v === null ? '—' : v.toLocaleString('ko-KR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const pct = (v: number | null) => v === null ? '—' : `${v > 0 ? '+' : ''}${fmt(v)}%`;
const asKstDate = (d: Date): string => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
const kstWeek = (d: Date): string => new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Seoul', weekday: 'short' }).format(d);
const toDate = (s: unknown): string => { const raw = str(s); const match = raw.match(/^\d{4}-\d{2}-\d{2}/); if (match) return match[0]; const compact = raw.match(/^(\d{4})(\d{2})(\d{2})/); return compact ? `${compact[1]}-${compact[2]}-${compact[3]}` : ''; };
const recentSession = (date: string): boolean => {
  if (!date) return false;
  const t = Date.parse(`${date}T00:00:00+09:00`);
  const delta = (Date.now() - t) / 86400000;
  return Number.isFinite(delta) && delta >= -0.25 && delta <= 6.5;
};

type PublicQuoteAuthority = {
  price: number | null;
  changeRate: number | null;
  tradeDate: string;
  source: 'STOCK_OBSERVED' | 'STOCK_EOD' | 'CONFIRMED_CLOSE' | 'NO_DATA';
  ignoredOlderStock: boolean;
};

// The observed stock quote and the screener close come from separate authorities.
// Never let a dated but older observation mask a newer confirmed close.
// An undated price cannot be advertised as a current price.
function resolvePublicQuote(stock: AnyRow, snapshot: AnyRow | null | undefined): PublicQuoteAuthority {
  const observedPrice = valueOf(stock.currentPrice ?? stock.current_price);
  const stockDataSource = str(stock.priceSource ?? stock.price_source ?? stock.dataSource ?? stock.data_source).toUpperCase();
  const observedDate = toDate(stock.sessionDate ?? stock.session_date);
  const closePrice = valueOf(snapshot?.closePrice);
  const closeDate = toDate(snapshot?.tradeDate);
  const observedValid = observedPrice !== null && observedPrice > 0 && Boolean(observedDate);
  const closeValid = closePrice !== null && closePrice > 0 && Boolean(closeDate);
  const ignoredOlderStock = observedValid && closeValid && observedDate < closeDate;

  if (observedValid && (!closeValid || observedDate >= closeDate)) {
    return {
      price: observedPrice,
      changeRate: valueOf(stock.changeRate ?? stock.change_rate),
      tradeDate: observedDate,
      source: stockDataSource === 'EOD' ? 'STOCK_EOD' : 'STOCK_OBSERVED',
      ignoredOlderStock: false,
    };
  }
  if (closeValid) {
    return {
      price: closePrice,
      changeRate: valueOf(snapshot?.priceChangePct),
      tradeDate: closeDate,
      source: 'CONFIRMED_CLOSE',
      ignoredOlderStock,
    };
  }
  return { price: null, changeRate: null, tradeDate: '', source: 'NO_DATA', ignoredOlderStock: false };
}

function MarketWorkspaceV8({ authenticated, summary, dashboard, changes = [], query,
  onQuery, suggestions, onSelect, onSubmit, preview, previewLoading, previewError, stockResearch,
  exposureRefs, onWatch, onEvidence, onOpen, onNavigate }: Props) {
  const [brief, setBrief] = useState<PublicBrief | null>(null);
  const [briefState, setBriefState] = useState<'LOADING' | 'OK' | 'UNAVAILABLE'>('LOADING');
  const today = new Date();
  const weekday = kstWeek(today);
  const weekend = weekday === 'Sat' || weekday === 'Sun';
  const raw: AnyRow = summary ?? {};
  const context: AnyRow = raw.marketContext ?? raw.market_context ?? {};
  const currentDay = asKstDate(today);
  const publicDate = str(brief?.sessionDate);
  const sourceDate = toDate(context?.domesticIndices?.KOSPI?.updatedAt ?? context?.domesticIndices?.KOSDAQ?.updatedAt);
  const referenceDate = (brief?.status === 'OK' && recentSession(publicDate)) ? publicDate : recentSession(sourceDate) ? sourceDate : '';
  const refUsable = Boolean(referenceDate);

  useEffect(() => {
    let controller: AbortController | null = null;
    let stopRetrying = false;
    const refresh = () => {
      if (stopRetrying || document.visibilityState === 'hidden') return;
      controller?.abort();
      controller = new AbortController();
      const signal = controller.signal;
      fetch('/api/main/market-intelligence/brief/latest', { credentials: 'include', headers: { Accept: 'application/json' }, signal })
        .then(r => { if (!r.ok) {
          if ([401,403,404].includes(r.status)) stopRetrying = true;
          throw new Error(`PUBLIC_BRIEF_HTTP_${r.status}`);
        } return r.json(); })
        .then((json: PublicBrief) => { if (!signal.aborted) { setBrief(json); setBriefState(json.status === 'OK' ? 'OK' : 'UNAVAILABLE'); } })
        .catch(() => { if (!signal.aborted) { setBrief(null); setBriefState('UNAVAILABLE'); } });
    };
    refresh();
    const timer = window.setInterval(refresh, ['Sat','Sun'].includes(kstWeek(new Date())) ? 300_000 : 60_000);
    return () => { window.clearInterval(timer); controller?.abort(); };
  }, []);

  const indexFromContext = (key: 'KOSPI' | 'KOSDAQ'): MarketValue => {
    const r = (context?.domesticIndices ?? context?.domestic_indices ?? {})[key] ?? {};
    return { value: valueOf(r.value ?? r.indexValue ?? r.last), changeRate: valueOf(r.changeRate ?? r.change_rate), observedAt: str(r.updatedAt) };
  };
  const index = (key: 'KOSPI' | 'KOSDAQ'): MarketValue => {
    const row = (brief?.status === 'OK' && recentSession(publicDate) ? brief.indices?.[key] : null) ?? indexFromContext(key);
    const rowDate = toDate(row?.observedAt) || (brief?.indices?.[key] === row ? publicDate : '');
    if (!recentSession(rowDate)) return {};
    return row;
  };
  const kospi = index('KOSPI'); const kosdaq = index('KOSDAQ');
  const kRate = valueOf(kospi.changeRate);
  const validThemeRows = (Array.isArray(raw.themeFlows) ? raw.themeFlows : [])
    .filter((x: AnyRow) => str(x.themeName ?? x.theme_name) && valueOf(x.avgChangeRate ?? x.avg_change_rate) !== null)
    .filter((x: AnyRow) => ['GOOD','PARTIAL'].includes(str(x.dataStatus ?? x.data_status).toUpperCase()));
  const themes = [...validThemeRows].sort((a,b) => (valueOf(b.avgChangeRate ?? b.avg_change_rate) ?? 0) - (valueOf(a.avgChangeRate ?? a.avg_change_rate) ?? 0));
  const strong = themes[0]; const weak = themes.length > 1 ? themes[themes.length - 1] : null;
  const breadth = brief?.status === 'OK' && recentSession(publicDate) ? brief.breadth : undefined;
  const breadthPct = breadth && breadth.up + breadth.down > 0 ? breadth.up / (breadth.up + breadth.down) * 100 : null;
  const verifiedMovers = (brief?.status === 'OK' && recentSession(publicDate) ? brief.movers ?? [] : [])
    .filter(x => x.flowStatus === 'ALIGNED_PROVISIONAL' && x.foreignIntensityPct !== null);
  const conflictingStock = verifiedMovers.find(x => (x.priceChangePct ?? 0) > 0.1 && (x.foreignIntensityPct ?? 0) < -1);

  const sourceLabel = refUsable ? `${referenceDate} ${referenceDate === currentDay ? '관측' : '마지막 거래일'} 기준` : '확인 가능한 최근 거래일 데이터 부족';
  const publicIssues = (Array.isArray(raw.issues) ? raw.issues : []).filter((x: AnyRow) => recentSession(toDate(x.publishedAt ?? x.published_at ?? x.detectedAt))).slice(0, 3);
  const memberIssueSource = dashboard?.issues;
  const memberIssues = (Array.isArray(memberIssueSource) ? memberIssueSource : []).filter((x: AnyRow) => recentSession(toDate(x.publishedAt ?? x.detectedAt))).slice(0, 3);
  const issues = (authenticated ? memberIssues : publicIssues).filter((x: AnyRow) => str(x.title ?? x.headline));
  const meaningfulIssues = issues.map((x: AnyRow) => ({ title: str(x.title ?? x.headline), source: str(x.source ?? x.provider), at: str(x.publishedAt ?? x.detectedAt), url: str(x.sourceUrl ?? x.url) }));
  if (!meaningfulIssues.length && brief?.event?.status === 'EDITOR_VERIFIED_PUBLIC_LINK' && brief?.event?.headline && recentSession(toDate(brief.event.observedAt))) {
    meaningfulIssues.push({ title: brief.event.headline, source: brief.event.source || '편집 확인된 외부 출처', at: str(brief.event.observedAt), url: str(brief.event.url) });
  }
  const holdings = (dashboard?.stocks ?? []).filter((x: AnyRow) => x.isHolding);
  // Market radar candidates are not saved/watchlisted stocks.
  const watchRows: AnyRow[] = [];
  const critical = holdings.filter((x: AnyRow) => ['HIGH_RISK','DEFENSIVE'].includes(str(x.riskAssessment?.state)));
  const personalRows = [...critical, ...holdings.filter((x: AnyRow) => !critical.includes(x)), ...watchRows].slice(0,4);
  const holdingTickers = new Set(holdings.map((x: AnyRow) => str(x.ticker)));
  // A generic market change does not become a personal change without ticker lineage.
  const recentChanges = changes.filter((item: AnyRow) => {
    const direct = str(item.ticker ?? item.stockCode ?? item.stock_code ?? item.stock?.ticker);
    const relation = str(item.entityKey ?? item.entity_key);
    const ticker = direct.replace(/^A(?=\d{6}$)/, '') || relation.match(/(?:^|[^0-9])(\d{6})(?:$|[^0-9])/)?.[1] || '';
    return holdingTickers.has(ticker);
  }).slice(0,3);
  const stock = preview?.stock ?? {};
  const ticker = str(stock.ticker ?? stock.stockCode ?? stock.stock_code);
  const stockName = str(stock.corpName ?? stock.corp_name ?? stock.name) || ticker;
  const publicQuote = resolvePublicQuote(stock, preview?.confirmedSnapshot);
  const stockDate = publicQuote.tradeDate;
  const stockRecent = recentSession(stockDate);
  // A stale or undated quote must not appear as the current publicly verified price.
  const price = stockRecent ? publicQuote.price : null;
  const priceRate = stockRecent ? publicQuote.changeRate : null;
  const quoteLabel = publicQuote.source === 'STOCK_OBSERVED' ? '종목 시세 관측가'
    : publicQuote.source === 'STOCK_EOD' ? '최근 일봉 종가'
    : publicQuote.source === 'CONFIRMED_CLOSE' ? '최근 확인 종가' : '가격 출처 확인 필요';
  const valuation = str(preview?.valueJudgment?.valueState ?? preview?.valueJudgment?.state ?? '');
  const valuationLabels: Record<string,string> = { VALUE_PRESERVED: '가치 보존', PRESERVED: '가치 보존', STABLE: '안정', REVIEW: '재검토', REVIEW_REQUIRED: '재검토 필요', UNDERVALUED: '저평가 참고', OVERVALUED: '고평가 참고', UNKNOWN: '확인 필요', NO_DATA: '근거 부족' };
  const valuationDisplay = valuationLabels[valuation.toUpperCase()] ?? (/^[A-Z_]+$/.test(valuation) ? '' : valuation);
  const nextSessionChecks = [
    { index: '01', title: conflictingStock ? `${conflictingStock.name || conflictingStock.ticker} 외국인 매도와 주가의 괴리` : '외국인 매도 압력의 완화',
      explanation: conflictingStock
        ? `${publicDate} KRX 정규장 외국인 순매수 강도 ${pct(conflictingStock.foreignIntensityPct)}(잠정)에도 주가는 ${pct(conflictingStock.priceChangePct)}였습니다. 다음 장에 이 매도 압력이 누그러지는지 확인합니다.`
        : 'KRX 정규장 기준 외국인 순매수와 거래대금의 기준을 일치시키고, 다음 장 첫 수급이 반전되는지 확인합니다.' },
    { index: '02', title: '소수 주도주에서 업종 전반으로 확산',
      explanation: breadthPct !== null ? `관측 표본 상승 비율 ${fmt(breadthPct,1)}%가 다음 장에도 확대되는지 확인합니다. 이것은 전체 시장 상승종목 비율이 아닙니다.`
        : '주도 업종 상승률과 상승 종목 비중이 함께 개선되는지 비교합니다. 지수 상승만으로 확산을 확정하지 않습니다.' },
    { index: '03', title: '환율·해외 선물의 새 거래일 반응', explanation: '주말 최종 참고 시세를 실시간으로 오인하지 않고, 새 거래 세션에서 방향과 원천시각을 다시 확인합니다.' },
  ];
  const hasIndexSnapshot = valueOf(kospi.value) !== null || valueOf(kosdaq.value) !== null || breadthPct !== null;
  const smallTrendClass = (n: number | null) => n === null ? 'is-neutral' : n > 0 ? 'is-up' : n < 0 ? 'is-down' : 'is-neutral';


  return (
    <div className="pi2-page pi2-editorial-refresh">
      <style>{PI7_EMBEDDED_STYLES}</style>
      <main className="pi2-shell" id="pi2-main">
        {!authenticated ? (
          <>
            <section className="pi2-hero" aria-labelledby="pi2-hero-title">
              <div className="pi2-primary">
                <div className="pi2-overline"><span className="pi2-dot"/> 데이터로 확인하는 종목 판단 · PolicyIntel</div>
                <h1 id="pi2-hero-title">내 종목의 판단 근거,<br/>무엇이 달라졌을까?</h1>
                <p>가격 움직임만으로 판단하지 마세요. 재무·수급·공시에서 확인된 근거와 다음 점검 조건을 한 화면에서 비교하세요.</p>
                <form className="pi2-search" onSubmit={(event)=>{event.preventDefault();onSubmit();}}>
                  <label htmlFor="pi2-stock-input" className="pi2-search-label">관심 종목의 핵심 근거를 바로 확인하세요</label>
                  <div className="pi2-search-control">
                    <input id="pi2-stock-input" value={query} onChange={event=>onQuery(event.target.value)}
                      placeholder="종목명 또는 6자리 코드 · 예: 삼성전자" autoComplete="off" />
                    <button type="submit" disabled={previewLoading}>분석 보기</button>
                  </div>
                  {query && suggestions.length > 0 && (!ticker || (query !== stockName && query !== ticker)) &&
                    <ul className="pi2-suggestions" aria-label="종목 검색 결과">
                      {suggestions.slice(0,6).map(stockRow=><li key={stockRow.ticker}><button type="button" onClick={()=>onSelect(stockRow)}>
                        <strong>{stockRow.corpName}</strong><span>{stockRow.ticker} · {stockRow.market ?? ''}</span>
                      </button></li>)}
                    </ul>}
                  <div className="pi2-inline-note">핵심 근거는 로그인 없이 볼 수 있습니다. 감시 등록 시 로그인이 필요합니다.</div>
                </form>
              </div>
              <aside className="pi2-side pi2-evidence-aside" aria-label="검증 가능한 분석 안내">
                <div>
                  <span className="pi2-eyebrow">{ticker ? `${stockName} · 분석 근거` : '어떤 결과를 확인하나요?'}</span>
                  <h2>{ticker ? '확인된 사실부터 살펴보세요.' : <>종목별 근거를<br/>먼저 확인하세요.</>}</h2>
                  <p>{ticker ? '분석 판단과 원천 자료의 확인 상태를 구분합니다.' : '종목을 검색하면 확인된 수치와 판단 유보 사유를 분리해 보여줍니다. 가입은 감시 등록 단계에서 필요합니다.'}</p>
                </div>
                <div className="pi2-evidence-aside__facts">
                  {ticker ? <>
                    <div className="pi2-side-stat"><span>가격 기준일</span><strong>{stockDate || '기준일 확인 중'}</strong></div>
                    {stockResearch?.financialReady && stockResearch.financialMetrics?.length ?
                      <div className="pi2-side-stat"><span>{stockResearch.financialMetrics[0].label}</span><strong>{stockResearch.financialMetrics[0].value}</strong></div>
                      : <div className="pi2-side-stat"><span>재무 근거</span><strong>추가 검증 필요</strong></div>}
                    {stockResearch?.checks?.length ? <div className="pi2-side-stat pi2-side-stat--long"><span>재검토 조건</span><strong>{stockResearch.checks[0]}</strong></div> : null}
                  </> : <>
                    <div className="pi2-side-stat"><span>01</span><strong>확인된 데이터</strong></div>
                    <div className="pi2-side-stat"><span>02</span><strong>판단의 한계</strong></div>
                    <div className="pi2-side-stat"><span>03</span><strong>다음 점검 조건</strong></div>
                  </>}
                </div>
              </aside>
            </section>
            <section className="pi2-section" aria-label="무료 종목 분석 결과" id="pi2-stock-result" aria-live="polite">
              {previewLoading && <div className="pi2-state" role="status">종목의 시세·기업 근거·공시·재무 데이터를 확인 중입니다.</div>}
              {!previewLoading && previewError && <div className="pi2-state" role="alert">{previewError}</div>}
              {!previewLoading && !previewError && ticker && <div className="pi2-result" ref={exposureRefs?.price}>
                <div className="pi2-result-header">
                  <div><span className="pi2-eyebrow">PUBLIC STOCK CHECK · 실데이터</span><h3>{stockName} <small>{ticker}</small></h3>
                    <small>기준 거래일: {stockDate || '확인 필요'} · {quoteLabel} · 확인되지 않은 값은 예측하지 않습니다.</small></div>
                  <div className="pi2-price"><strong>{price === null ? '가격 확인 필요' : `${fmt(price,0)}원`}</strong>
                    <div className={stockRecent ? smallTrendClass(priceRate) : 'is-neutral'}>
                      {stockRecent ? (priceRate === null ? '등락률 확인 필요' : pct(priceRate)) : (stockDate ? '오래된 거래일 자료 · 최신 가격 확인 필요' : '가격 기준일 확인 필요')}
                    </div></div>
                </div>
                <div className="pi2-observed-evidence" aria-label="확인된 기초 사실">
                  <strong>먼저 확인된 사실</strong>
                  <div className="pi2-observed-evidence__items">
                    {stockDate && price !== null ? <span>기준일 {stockDate} · 표시 가격 {fmt(price,0)}원</span> : null}
                    {stockResearch?.financialReady && stockResearch.financialMetrics?.length ?
                      stockResearch.financialMetrics.slice(0,2).map(item=><span key={item.label}>{item.label} {item.value}{stockResearch.financialBasisDate ? ` · 재무 ${stockResearch.financialBasisDate}` : ''}</span>) : null}
                  </div>
                  {!(stockResearch?.financialReady && stockResearch.financialMetrics?.length) ?
                    <p>공개 검증된 재무 근거가 제한적입니다. 공시의 기업 연결·판단 영향은 별도로 검증해야 하므로 여기서는 확정 근거로 승격하지 않습니다.</p> : null}
                </div>
                <div className="pi2-result-grid">
                  <div className="pi2-fact" ref={exposureRefs?.value}><span>01 · 기업가치 판단 상태</span><strong>{valuation.toUpperCase() === 'LIMITED' ? '판단 유보' : (valuationDisplay || stockResearch?.fundamental || '판단 근거 확인 중')}</strong>
                    <p>가격·수급과 기업가치의 상태를 구분해 확인합니다.</p></div>
                  <div className="pi2-fact" ref={exposureRefs?.reason}><span>02 · 왜 그렇게 판단했나요?</span><strong>{stockResearch?.summary || '확인 가능한 판단 근거를 점검 중입니다.'}</strong>
                    <p>{stockResearch?.fundamental || '재무·회사 근거 확인 필요'}</p></div>
                  <article className="pi2-fact"><span>03 · 이전과 달라진 것</span><strong>{stockResearch?.change || '최근 판단 변화 정보 확인 중'}</strong>
                    <p>{stockResearch?.disclosureTitle ? `최근 공시: ${stockResearch.disclosureTitle} · ${stockResearch.disclosureDate || '일자 확인 필요'}` : stockResearch?.disclosureEmptyText || '공시 관측 상태 확인 중'}</p></article>
                  <div className="pi2-fact pi2-next" ref={exposureRefs?.condition}><span>04 · 다음 확인 조건</span><strong>{stockResearch?.checks?.[0] || '근거가 확인된 재검토 조건이 아직 없습니다.'}</strong>
                    {stockResearch?.checks && stockResearch.checks.length>1 && <p>추가 조건: {stockResearch.checks.slice(1).join(' · ')}</p>}</div>
                </div>
                {stockResearch?.financialMetrics && stockResearch.financialMetrics.length>0 &&
                  <div className="pi2-marketrow" style={{marginTop:19}}><span>확인된 재무지표 · {stockResearch.financialBasisDate || '기준일 확인 필요'}</span>
                    <strong style={{fontSize:13}}>{stockResearch.financialMetrics.slice(0,3).map(item=>`${item.label} ${item.value}`).join(' · ')}</strong></div>}
                <div className="pi2-result-actions">
                  <button type="button" className="pi2-primary-action" ref={exposureRefs?.trackCta} onClick={()=>onWatch(ticker,stockName)}>이 종목 계속 감시하기</button>
                  <button type="button" className="pi2-secondary-action" onClick={()=>onNavigate(`/stocks/${ticker}`)}>공개 근거 자세히 보기</button>
                </div>
                <p style={{color:'#7B899A',fontSize:12,margin:'13px 0 0'}}>지속 감시를 선택하면 로그인 후에도 선택한 종목을 이어서 확인합니다.</p>
              </div>}
            </section>
          </>
        ) : (
          <>
            <section className="pi2-hero" aria-labelledby="pi2-hero-title">
              <div className="pi2-primary"><span className="pi2-eyebrow">TODAY BRIEF · {currentDay} KST</span>
                <h1 id="pi2-hero-title">최근 내 종목에서,<br/>무엇이 달라졌나?</h1>
                <p>{recentChanges.length>0 ? `확인된 변화 ${recentChanges.length}건부터 검토하세요. 가격 움직임과 보유 논리 변경은 서로 다릅니다.` : '등록된 보유종목과 연결된 새 판단 변화가 확인되지 않았습니다. 위험이 없다는 뜻은 아니며, 새 근거가 들어오면 다시 확인합니다.'}</p>
                <button type="button" className="pi2-primary-action" onClick={()=>onNavigate('/watch')}>내 종목 확인하기</button>
              </div>
              <aside className="pi2-side"><div><span className="pi2-eyebrow">MY WATCH</span><h2>변화가 필요한 것만 먼저.</h2>
                <p>보유종목의 위험 조건과 새 판단 근거를 먼저 확인합니다.</p></div>
                <div><div className="pi2-side-stat"><span>보유 종목</span><strong>{holdings.length}개</strong></div>
                  <div className="pi2-side-stat"><span>확인할 위험</span><strong>{critical.length}개</strong></div>
                  <div className="pi2-side-stat"><span>최근 변화</span><strong>{recentChanges.length}건</strong></div></div></aside>
            </section>
            <section className="pi2-section" aria-label="최근 변화">
              <div className="pi2-sectionhead"><h2>지금 확인할 변화</h2><p>원인을 확정할 수 없는 항목은 판단 보류</p></div>
              <div className="pi2-watch-list">{recentChanges.length > 0 ? recentChanges.map((item,i)=><button type="button" className="pi2-watch-row" key={str(item.id)||i}
                onClick={()=>onOpen('HOLDING','today-brief-change')}><strong>{str(item.title ?? item.message ?? item.eventType)||`변화 ${i+1}`}</strong><small>{str(item.reason ?? item.message)||'근거 확인 중'}</small><em>변화 보기 →</em></button>)
              : <div className="pi2-state">오늘 확인된 새 판단 변화는 없습니다. 감시 상태를 계속 확인합니다.</div>}</div>
            </section>
            <section className="pi2-section" aria-label="내 종목 상태"><div className="pi2-sectionhead"><h2>감시 중인 종목</h2><p>확인 필요 → 변화 → 안정</p></div>
              <div className="pi2-watch-list">{personalRows.length>0?personalRows.map((item:AnyRow)=><button type="button" className="pi2-watch-row" key={item.ticker}
                onClick={()=>onOpen(item.isHolding?'HOLDING':'STOCK','today-brief-stock',{ticker:item.ticker})}><strong>{item.name||item.ticker}</strong>
                <small>{str(item.compactStatusLabel ?? item.statusLabel)||'판단 상태 확인 중'}</small><em className={smallTrendClass(valueOf(item.changeRate))}>{pct(valueOf(item.changeRate))}</em></button>)
              : <div className="pi2-state">보유종목이 아직 없습니다. 관심 종목은 내 종목에서 연결 상태를 확인할 수 있습니다.</div>}</div>
            </section>
          </>
        )}
        {authenticated && (
          <section className="pi2-section" aria-label="10분 검증 관측">
            <div className="pi2-sectionhead"><h2>10분 시장 검증</h2><p>기존 실시간 수집 기준 · 진단 전용</p></div>
            <P1ShadowEvidenceCard shadow={raw.liveIssueSignal?.p1_shadow ?? null} macro={raw.intelligenceBridge?.macro} compact />
          </section>
        )}
        {(authenticated || hasIndexSnapshot) && <section className="pi2-section" aria-label="시장 근거">
          <div className="pi2-sectionhead"><h2>{weekend?'지난 거래일 시장 점검':'현재 시장 근거'}</h2><p>{sourceLabel}</p></div>
          <div className="pi2-market">
            <article className="pi2-marketpanel"><span className="pi2-eyebrow">MARKET CONTEXT</span><h3>국내 지수와 표본</h3>
              {hasIndexSnapshot ? <>
                <div className="pi2-marketrow"><span>KOSPI</span><strong>{fmt(valueOf(kospi.value))} <em className={smallTrendClass(kRate)}>{pct(kRate)}</em></strong></div>
                <div className="pi2-marketrow"><span>KOSDAQ</span><strong>{fmt(valueOf(kosdaq.value))} <em className={smallTrendClass(valueOf(kosdaq.changeRate))}>{pct(valueOf(kosdaq.changeRate))}</em></strong></div>
                <div className="pi2-marketrow"><span>수집 종목 상승 비율</span><strong>{breadthPct===null?'자료 없음':`${fmt(breadthPct,1)}%`}</strong></div>
              </> : <div className="pi2-state">검증된 최근 정규장 지표가 연결되지 않았습니다. 추정값을 표시하지 않습니다.</div>}
              {strong && <div className="pi2-marketrow"><span>관측 상대강세 업종</span><strong>{str(strong.themeName??strong.theme_name)}</strong></div>}
              {weak && <div className="pi2-marketrow"><span>관측 상대약세 업종</span><strong>{str(weak.themeName??weak.theme_name)}</strong></div>}
              <button type="button" className="pi2-secondary-action" onClick={()=>onOpen('SUMMARY','today-brief-market')} style={{marginTop:12}}>시장 근거 자세히</button>
            </article>
            <article className="pi2-marketpanel"><span className="pi2-eyebrow">NEXT SESSION</span><h3>다음에 확인할 조건</h3>
              {refUsable && nextSessionChecks.slice(0,3).map(check=><div className="pi2-marketrow" key={check.index} style={{alignItems:'start'}}><span style={{maxWidth:'100%'}}><strong style={{fontSize:13}}>{check.index} · {check.title}</strong>
                <div style={{fontSize:12,lineHeight:1.75,marginTop:5}}>{check.explanation}</div></span></div>)}
              {meaningfulIssues.length>0 && <div className="pi2-marketrow"><span>확인된 최신 이슈</span><strong>{meaningfulIssues.length}건</strong></div>}
            </article>
          </div>
        </section>}
        {!authenticated && !hasIndexSnapshot && <aside className="pi2-market-compact" role="note">
          <strong>시장 브리핑은 최근 정규장 지표 검증 후 공개합니다.</strong>
          <span>자료가 부족한 상황에서도 종목별 확인 가능한 근거는 위 검색으로 살펴볼 수 있습니다.</span>
        </aside>}
        {!authenticated && <section className="pi2-section pi2-method-note" aria-label="분석 원칙">
          <strong>분석 원칙</strong>
          <p>확인된 가격·사업·재무·수급 사실을 먼저 보여주고, 근거가 부족하면 판단을 유보합니다. 종목을 감시하면 이후 상태를 이어서 확인할 수 있습니다.</p>
        </section>}
        <footer className="pi2-footer">
          <strong>PolicyIntel · Risk & Change Assistant</strong>
          <span>{sourceLabel} · 데이터 공백은 0이나 예측값으로 대체하지 않습니다.</span>
          <span>{QUASI_ADVISORY_REPORTED ? `${QUASI_ADVISORY_BUSINESS_NAME} · 유사투자자문업 신고번호 ${QUASI_ADVISORY_REPORT_NO} · 금융투자상품 원금손실 가능` : '금융정보 참고 서비스이며 유사투자자문업 신고 수리 상태를 완료로 표시하지 않습니다.'}</span>
          {authenticated && <button type="button" onClick={()=>onNavigate('/screener')}>전문 스크리너 열기 →</button>}
        </footer>
        {briefState==='UNAVAILABLE' && <span className="pi2-overline">공개 시장 브리핑 일부 원천을 확인할 수 없습니다.</span>}
      </main>
    </div>
  );

}

// POLICYINTEL_V8_SIGNAL_WORKSPACE_END

type Tone = 'good' | 'up' | 'warn' | 'down' | 'neutral';

type CommandMetric = {
  eyebrow: string;
  label: string;
  value: string;
  sub: string;
  tone: Tone;
  locked?: boolean;
};

type PriorityItem = {
  order: number;
  category: string;
  title: string;
  description: string;
  action: string;
  tab: LiveSummaryTab;
  tone: Tone;
};

type MarketPreviewCheck = {
  label: string;
  value: string;
  tone: Tone;
};

type MarketPreview = {
  mode: 'live' | 'prepare' | 'pending' | 'guest';
  eyebrow: string;
  badge: string;
  title: string;
  description: string;
  actionTitle: string;
  actionDescription: string;
  tone: Tone;
  checks: MarketPreviewCheck[];
  ctaLabel: string;
  tab: LiveSummaryTab;
};

type MarketPreviewExtras = {
  leadThemeName?: string;
  leadThemeChangeRate?: number | null;
  watchCandidateCount?: number;
  opportunityCount?: number;
};

type CommandBoard = {
  generatedAt: string;
  marketStatus: string;
  leadThemeName: string;
  leadThemeChangeRate: number | null;
  issueCount: number;
  urgentCount: number;
  opportunityCount: number;
  watchCandidateCount: number;
  metrics: CommandMetric[];
  priorities: PriorityItem[];
  marketPreview: MarketPreview;
};

const MAIN_AUTO_REFRESH_MS = 60_000;
const TOP_RISING_THEME_LIMIT = 10;
const THEME_STOCK_FETCH_LIMIT = 100;




const QUASI_ADVISORY_BUSINESS_NAME = String(
  process.env.REACT_APP_QUASI_ADVISORY_BUSINESS_NAME ?? '',
).trim();
const QUASI_ADVISORY_REPORT_NO = String(
  process.env.REACT_APP_QUASI_ADVISORY_REPORT_NO ?? '',
).trim();
const QUASI_ADVISORY_REPORTED = String(
  process.env.REACT_APP_QUASI_ADVISORY_REPORTED ?? '',
).trim().toLowerCase() === 'true'
  && Boolean(QUASI_ADVISORY_BUSINESS_NAME)
  && Boolean(QUASI_ADVISORY_REPORT_NO);

function cleanText(value: unknown): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

function nullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function createClientIntentId(prefix: string): string {
  try {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
      return `${prefix}:${globalThis.crypto.randomUUID()}`;
    }
  } catch {
    // 제한된 브라우저 환경에서는 아래 fallback을 사용한다.
  }
  return `${prefix}:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 10)}`;
}


function topRisingThemes(response: LiveSummaryApiResponse | null): any[] {
  if (!response) return [];
  const raw = response as any;
  const rows = Array.isArray(raw?.themeFlows) ? raw.themeFlows : [];

  return [...rows]
    .filter((theme: any) => {
      const code = cleanText(theme?.themeCode ?? theme?.theme_code);
      const rate = nullableNumber(theme?.avgChangeRate ?? theme?.avg_change_rate);
      const status = cleanText(theme?.dataStatus ?? theme?.data_status).toUpperCase();
      return Boolean(code)
        && rate !== null
        && rate > 0
        && (!status || status === 'GOOD' || status === 'PARTIAL');
    })
    .sort((left: any, right: any) => {
      const rateGap = (nullableNumber(right?.avgChangeRate ?? right?.avg_change_rate) ?? -Infinity)
        - (nullableNumber(left?.avgChangeRate ?? left?.avg_change_rate) ?? -Infinity);
      if (rateGap !== 0) return rateGap;

      const smartMoneyGap = (nullableNumber(right?.smartMoneyNetBuyAmount ?? right?.smart_money_net_buy_amount) ?? 0)
        - (nullableNumber(left?.smartMoneyNetBuyAmount ?? left?.smart_money_net_buy_amount) ?? 0);
      if (smartMoneyGap !== 0) return smartMoneyGap;

      const breadthGap = (nullableNumber(right?.smartMoneyBreadthPct ?? right?.smart_money_breadth_pct) ?? 0)
        - (nullableNumber(left?.smartMoneyBreadthPct ?? left?.smart_money_breadth_pct) ?? 0);
      if (breadthGap !== 0) return breadthGap;

      return cleanText(left?.themeName ?? left?.theme_name)
        .localeCompare(cleanText(right?.themeName ?? right?.theme_name), 'ko');
    })
    .slice(0, TOP_RISING_THEME_LIMIT);
}

function mergeTopThemeStocks(
  themes: any[],
  rowsByTheme: Array<{ theme: any; rows: LiveSummaryThemeStock[] }>,
): any[] {
  const rankByCode = new Map<string, number>();
  themes.forEach((theme: any, index: number) => {
    const code = cleanText(theme?.themeCode ?? theme?.theme_code);
    if (code) rankByCode.set(code, index + 1);
  });

  const byTicker = new Map<string, any>();
  rowsByTheme.forEach(({theme, rows}) => {
    const themeCode = cleanText(theme?.themeCode ?? theme?.theme_code);
    const themeName = cleanText(theme?.themeName ?? theme?.theme_name) || themeCode;
    const themeChangeRate = nullableNumber(theme?.avgChangeRate ?? theme?.avg_change_rate);
    const themeRank = rankByCode.get(themeCode) ?? TOP_RISING_THEME_LIMIT + 1;

    rows.forEach((row: LiveSummaryThemeStock) => {
      const ticker = cleanText(row?.ticker);
      if (!/^\d{6}$/.test(ticker)) return;

      const previous = byTicker.get(ticker);
      const previousRank = Number(previous?.themeRank ?? Number.MAX_SAFE_INTEGER);
      const mergedThemeNames = Array.from(new Set([
        ...(Array.isArray(previous?.themeNames) ? previous.themeNames : []),
        themeName,
      ].filter(Boolean)));

      const candidate = {
        ...(previous ?? {}),
        ...row,
        ticker,
        themeCode: themeRank <= previousRank ? themeCode : previous?.themeCode,
        themeName: themeRank <= previousRank ? themeName : previous?.themeName,
        themeChangeRate: themeRank <= previousRank ? themeChangeRate : previous?.themeChangeRate,
        themeRank: Math.min(themeRank, previousRank),
        themeNames: mergedThemeNames,
      };
      byTicker.set(ticker, candidate);
    });
  });

  return Array.from(byTicker.values()).sort((left, right) => {
    const rankGap = Number(left?.themeRank ?? 999) - Number(right?.themeRank ?? 999);
    if (rankGap !== 0) return rankGap;
    const valueGap = (nullableNumber(right?.tradingValue) ?? 0) - (nullableNumber(left?.tradingValue) ?? 0);
    if (valueGap !== 0) return valueGap;
    return (nullableNumber(right?.changeRate) ?? 0) - (nullableNumber(left?.changeRate) ?? 0);
  });
}

function formatPct(value: number | null): string {
  if (value === null) return '-';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function shortTime(value: unknown): string {
  const raw = cleanText(value);
  if (!raw) return '방금 갱신';
  const normalized = raw.replace('T', ' ');
  const match = normalized.match(/(\d{2}):(\d{2})(?::\d{2})?/);
  return match ? `${match[1]}:${match[2]} 기준` : raw;
}

function getSeoulMarketClock(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);

  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';

  return {
    weekday: value('weekday'),
    hour: Number(value('hour')),
    minute: Number(value('minute')),
  };
}

function isDomesticStockTradingTime(now = new Date()) {
  const { weekday, hour, minute } = getSeoulMarketClock(now);
  if (weekday === 'Sat' || weekday === 'Sun') return false;
  const minutes = hour * 60 + minute;
  const krxRegular = minutes >= 9 * 60 && minutes <= 15 * 60 + 30;
  const nxtAfter = minutes >= 15 * 60 + 40 && minutes <= 20 * 60;
  return krxRegular || nxtAfter;
}

function toneFromText(value: unknown): Tone {
  const text = cleanText(value).toUpperCase();
  if (/DANGER|CRITICAL|HIGH RISK|위험|급락|붕괴|이탈/.test(text)) return 'down';
  if (/SHORT.COVER|숏커버|CAUTION|WATCH|주의|경계|약화|보류|반전/.test(text)) return 'warn';
  if (/BULL|STRONG|UPTREND|상승|강세|동행|우호/.test(text)) return 'up';
  if (/GOOD|SAFE|STABLE|정상|안정/.test(text)) return 'good';
  return 'neutral';
}

function marketStatusLabel(rawLabel: unknown, publicContext: any): string {
  const headline = cleanText(
    publicContext?.headline
      ?? publicContext?.structureLabel
      ?? publicContext?.structure_label
      ?? publicContext?.directionLabel
      ?? publicContext?.direction_label,
  );
  if (headline) return headline;

  const label = cleanText(rawLabel).toUpperCase();
  if (label.includes('RISK ON')) return '공격 우위';
  if (label.includes('RISK OFF')) return '방어 우위';
  if (label.includes('BULL') || label.includes('STRONG')) return '강세 우위';
  if (label.includes('BEAR') || label.includes('WEAK')) return '주의 우위';
  return '중립';
}

function marketTone(rawLabel: unknown, publicContext: any): Tone {
  return toneFromText(
    `${cleanText(rawLabel)} ${cleanText(publicContext?.headline)} ${cleanText(publicContext?.structureLabel)} ${cleanText(publicContext?.actionLabel)} ${cleanText(publicContext?.transitionLabel)}`,
  );
}

function scoreBand(value: unknown, kind: 'quality' | 'risk' | 'pressure'): string {
  const score = nullableNumber(value);
  if (score === null) return '계산 중';
  if (kind === 'risk') {
    if (score >= 70) return '높음';
    if (score >= 45) return '보통';
    return '낮음';
  }
  if (kind === 'quality') {
    if (score >= 70) return '높음';
    if (score >= 45) return '보통';
    return '낮음';
  }
  if (score >= 60) return '상승 우위';
  if (score <= 40) return '하락 우위';
  return '중립';
}

function marketPhaseState(raw: any, market: any, context: any): 'live' | 'prepare' | 'pending' {
  const stockSession = cleanText(raw?.marketSession).toUpperCase();
  const phase = cleanText(
    context?.sessionPhase
      ?? context?.session_phase
      ?? context?.phase
      ?? market?.phase
      ?? market?.marketPhase
      ?? raw?.marketPhase
      ?? raw?.phase,
  ).toUpperCase();
  const stale = Boolean(context?.stale ?? context?.isStale ?? context?.is_stale);
  const available = context?.available !== false && context?.dataStatus !== 'NO_DATA' && context?.data_status !== 'NO_DATA';

  // 국내 현물 가격 세션은 KRX 분석 phase와 분리한다.
  if (stockSession === 'NXT_AFTER' || stockSession === 'KRX_REGULAR') return 'live';
  if (stockSession === 'NXT_TRANSITION') return 'prepare';

  if (/HOLIDAY|CLOSED|AFTER|PREMARKET|PRE_MARKET|OFF|END|장외|휴장|마감/.test(phase)) return 'prepare';
  if (stale && !/OPEN|LIVE|REGULAR|TRADING/.test(phase)) return 'prepare';
  if (/OPEN|LIVE|REGULAR|TRADING|장중/.test(phase)) return available ? 'live' : 'pending';

  if (!isDomesticStockTradingTime()) return 'prepare';
  return available ? 'live' : 'pending';
}

function buildMarketPreview(
  raw: any,
  market: any,
  context: any,
  extras: MarketPreviewExtras = {},
): MarketPreview {
  const mode = marketPhaseState(raw, market, context);
  const leadThemeName = cleanText(extras.leadThemeName) || '테마 확인 중';
  const leadThemeChangeRate = extras.leadThemeChangeRate ?? null;
  const watchCandidateCount = Math.max(0, Number(extras.watchCandidateCount ?? 0) || 0);
  const opportunityCount = Math.max(0, Number(extras.opportunityCount ?? 0) || 0);
  const leadThemeLabel = leadThemeName === '테마 확인 중'
    ? '강한 테마 확인 중'
    : `${leadThemeName}${leadThemeChangeRate === null ? '' : ` ${formatPct(leadThemeChangeRate)}`}`;
  const headline = marketStatusLabel(market?.label, context);
  const contextTone = marketTone(market?.label, context);
  const contextText = cleanText(
    context?.summary
      ?? context?.description
      ?? context?.comment
      ?? context?.publicSummary
      ?? context?.public_summary,
  );
  const transitionText = cleanText(context?.transitionLabel ?? context?.transition_label);
  const actionText = cleanText(
    context?.actionLabel
      ?? context?.action_label
      ?? context?.actionGuide
      ?? context?.action_guide,
  );
  const stockSession = cleanText(raw?.marketSession).toUpperCase();

  if (stockSession === 'NXT_AFTER') {
    return {
      mode: 'live',
      eyebrow: 'NXT AFTER',
      badge: 'NXT 애프터',
      title: 'NXT 애프터 거래가 진행 중입니다. 종목 가격 흐름을 KRX 마감 수급과 함께 확인하세요.',
      description: '현재가·거래량·거래대금은 NXT 실시간 기준으로 갱신하고, 시장 지수·외국인·기관·프로그램 수급은 KRX 정규장 마감 기준으로 구분해 봅니다.',
      actionTitle: '현재 대응',
      actionDescription: 'NXT 가격 변화만으로 KRX 수급 방향이 바뀐 것으로 해석하지 말고, 가격 이탈·회복 여부를 우선 확인하세요.',
      tone: contextTone,
      checks: [
        { label: '가격 세션', value: 'NXT 실시간', tone: 'up' },
        { label: '수급 기준', value: 'KRX 15:30 마감', tone: 'neutral' },
      ],
      ctaLabel: 'NXT 가격·KRX 수급 확인',
      tab: 'SUMMARY',
    };
  }

  if (mode === 'prepare') {
    const leadWasStrong = leadThemeChangeRate !== null && leadThemeChangeRate > 0;
    const candidateLabel = opportunityCount > 0
      ? `기회 후보 ${opportunityCount}개`
      : watchCandidateCount > 0
        ? `관찰 후보 ${watchCandidateCount}개`
        : '후보 정리 중';

    return {
      mode,
      eyebrow: 'TODAY FLOW',
      badge: '장후 정리',
      title: leadWasStrong
        ? `오늘 ${leadThemeName} 테마가 ${formatPct(leadThemeChangeRate)}로 강했습니다. 어떤 종목에 수급이 붙었는지 확인하세요.`
        : '오늘 돈이 어디로 움직였는지 정리했습니다. 내일 먼저 볼 후보를 확인하세요.',
      description: '장 마감 후에는 새 신호를 현재값처럼 단정하지 않고, 오늘 강했던 테마와 종목 수급을 내일 관찰 후보로 정리합니다.',
      actionTitle: '내일 먼저 볼 것',
      actionDescription: opportunityCount > 0
        ? `오늘 계산된 기회 후보 ${opportunityCount}개와 시장·테마 정합성을 함께 확인하세요.`
        : '등락률만 보지 말고 외국인·기관 수급, VWAP 위치, 테마 확산 강도가 함께 남아 있는지 확인하세요.',
      tone: leadWasStrong ? 'up' : 'neutral',
      checks: [
        { label: '오늘 강한 테마', value: leadThemeLabel, tone: leadWasStrong ? 'up' : 'neutral' },
        { label: '관찰 후보', value: candidateLabel, tone: opportunityCount > 0 || watchCandidateCount > 0 ? 'up' : 'neutral' },
      ],
      ctaLabel: '오늘 수급·관찰 후보 확인',
      tab: 'SUMMARY',
    };
  }

  if (mode === 'pending') {
    return {
      mode,
      eyebrow: 'MARKET CONTEXT',
      badge: '첫 신호 확인 중',
      title: '첫 수급 신호가 잡히는 동안, 추격보다 확인 순서를 먼저 보세요.',
      description: '데이터가 충분히 쌓이기 전에는 방향을 단정하지 않습니다. 초기 수급과 구조가 확인되는 순간 무엇을 볼지 먼저 정리합니다.',
      actionTitle: '현재 대응',
      actionDescription: '첫 수급 확인 전에는 계획 가격과 손절 기준을 유지하고, 시장·테마·수급이 함께 정렬되는지 확인하세요.',
      tone: 'neutral',
      checks: [
        { label: '흐름 신뢰', value: '계산 중', tone: 'neutral' },
        { label: '추격 위험', value: '계산 중', tone: 'neutral' },
      ],
      ctaLabel: '첫 시장·수급 신호 확인',
      tab: 'SUMMARY',
    };
  }

  const flowQuality = scoreBand(context?.flowQualityScore ?? context?.flow_quality_score, 'quality');
  const entryRisk = scoreBand(context?.entryRiskScore ?? context?.entry_risk_score, 'risk');
  const entryRiskScore = nullableNumber(context?.entryRiskScore ?? context?.entry_risk_score);
  const riskTone: Tone = entryRiskScore !== null && entryRiskScore >= 70
    ? 'warn'
    : entryRiskScore !== null && entryRiskScore < 40
      ? 'good'
      : 'neutral';

  return {
    mode,
    eyebrow: 'MARKET CONTEXT',
    badge: actionText || transitionText || (contextTone === 'warn' ? '추격 주의' : contextTone === 'up' ? '흐름 확인' : '시장 확인'),
    title: headline || '시장 구조 확인 중',
    description: contextText || '시장 움직임의 방향뿐 아니라 참여 강도와 추격 위험을 함께 확인합니다.',
    actionTitle: '현재 대응',
    actionDescription: actionText || (entryRiskScore !== null && entryRiskScore >= 70
      ? '추격보다 눌림과 지지 확인을 우선하세요.'
      : '계획 가격과 시장 구조가 함께 맞는지 확인하세요.'),
    tone: contextTone,
    checks: [
      { label: '흐름 신뢰', value: flowQuality, tone: flowQuality === '낮음' ? 'warn' : flowQuality === '높음' ? 'up' : 'neutral' },
      { label: '추격 위험', value: entryRisk, tone: riskTone },
    ],
    ctaLabel: entryRiskScore !== null && entryRiskScore >= 70
      ? '추격 위험·수급 근거 확인'
      : contextTone === 'up'
        ? '지금 진입 조건 확인'
        : '시장·수급 근거 확인',
    tab: 'SUMMARY',
  };
}

function stockRiskPriorityScore(stock: any): number {
  let score = 0;
  const riskScore = nullableNumber(stock?.riskScore ?? stock?.risk_score);
  if (riskScore !== null) score += Math.max(0, Math.min(100, riskScore)) * 3;

  const riskLevel = cleanText(stock?.riskLevel ?? stock?.risk_level ?? stock?.riskLabel).toUpperCase();
  if (/CRITICAL|DANGER|HIGH|위험/.test(riskLevel)) score += 220;
  else if (/MEDIUM|WARN|주의/.test(riskLevel)) score += 90;

  const text = `${cleanText(stock?.status)} ${cleanText(stock?.actionLabel)} ${cleanText(stock?.actionReason)} ${cleanText(stock?.flowLabel)} ${cleanText(stock?.judgementLabel)}`;
  if (/수급 이탈|매도 우세|반전|청산|위험|급변|주의/.test(text)) score += 140;

  const foreign = nullableNumber(stock?.foreignNetBuyAmount ?? stock?.foreign_net_buy_amount);
  const institution = nullableNumber(stock?.institutionNetBuyAmount ?? stock?.institution_net_buy_amount);
  if (foreign !== null && foreign < 0) score += 35;
  if (institution !== null && institution < 0) score += 35;

  const changeRate = nullableNumber(stock?.changeRate ?? stock?.change_rate);
  if (changeRate !== null) {
    if (Math.abs(changeRate) >= 15) score += 30;
    else if (Math.abs(changeRate) >= 8) score += 14;
  }

  return score;
}

function stockActionContext(stock: any, marketPreview: MarketPreview) {
  if (!stock) return { label: '수급·리스크 확인', tone: 'neutral' as Tone };
  const text = `${cleanText(stock?.riskLevel ?? stock?.riskLabel)} ${cleanText(stock?.status)} ${cleanText(stock?.actionLabel)} ${cleanText(stock?.actionReason)} ${cleanText(stock?.flowLabel)}`.toUpperCase();
  const riskScore = nullableNumber(stock?.riskScore ?? stock?.risk_score);
  const rate = nullableNumber(stock?.changeRate ?? stock?.change_rate);

  if ((riskScore !== null && riskScore >= 70) || /CRITICAL|DANGER|HIGH|위험|급변/.test(text)) {
    return { label: '리스크 급변 · 우선 점검', tone: 'down' as Tone };
  }
  if (/수급 이탈|매도 우세|반전|청산/.test(text)) {
    return { label: '수급 이탈 · 반전 확인', tone: 'warn' as Tone };
  }
  if (rate !== null && rate >= 15) {
    return { label: '급등 · 추격 위험 확인', tone: 'warn' as Tone };
  }
  if (marketPreview.tone === 'warn' || marketPreview.tone === 'down') {
    return { label: '시장 경계 · 종목 수급 확인', tone: 'warn' as Tone };
  }
  if (marketPreview.tone === 'up') {
    return { label: '종목·시장 동행 여부 확인', tone: 'up' as Tone };
  }
  return { label: '수급·리스크 확인', tone: 'neutral' as Tone };
}

function buildCommandBoard(
  response: LiveSummaryApiResponse,
  themeStocks: LiveSummaryThemeStock[] = [],
): CommandBoard {
  const raw = response as any;
  const market = raw?.marketState ?? {};
  // Public marketContext is preferred. derivativeSignal remains only as a backward-compatible fallback.
  const marketContext = raw?.marketContext ?? raw?.market_context ?? raw?.derivativeSignal ?? {};
  const themes = topRisingThemes(response);
  const holdings = Array.isArray(raw?.holdings) ? raw.holdings : [];
  const alerts = Array.isArray(raw?.alerts) ? raw.alerts : [];
  const issuesCandidate = raw?.issues ?? raw?.realtimeIssues ?? raw?.realtime_issues;
  const issues = Array.isArray(issuesCandidate) ? issuesCandidate : [];
  const opportunityRadar = raw?.opportunityRadar ?? raw?.opportunity_radar ?? {};
  const opportunityCandidates = Array.isArray(opportunityRadar?.candidates)
    ? opportunityRadar.candidates
    : [];

  const leadTheme = themes[0] ?? null;
  const leadThemeName = cleanText(leadTheme?.themeName ?? leadTheme?.theme_name) || '테마 확인 중';
  const leadThemeChangeRate = nullableNumber(leadTheme?.avgChangeRate ?? leadTheme?.avg_change_rate);

  const watchCandidateKeys = new Set<string>();
  themeStocks.forEach((stock: any) => {
    const key = cleanText(stock?.ticker ?? stock?.code ?? stock?.name);
    if (key) watchCandidateKeys.add(key);
  });

  const opportunityCount = opportunityCandidates.filter((candidate: any) =>
    nullableNumber(candidate?.opportunityScore ?? candidate?.opportunity_score) !== null,
  ).length;
  const watchCandidateCount = watchCandidateKeys.size;

  const marketPreview = buildMarketPreview(raw, market, marketContext, {
    leadThemeName,
    leadThemeChangeRate,
    watchCandidateCount,
    opportunityCount,
  });
  const marketStatus = marketPreview.mode === 'live' ? marketPreview.title : marketPreview.badge;
  const currentMarketTone = marketPreview.tone;

  const dangerHoldings = holdings.filter((item: any) => {
    const assessment = normalizeRiskAssessment(item?.riskAssessment, 'HOLDING');
    return assessment.available
      && (assessment.state === 'HIGH_RISK' || assessment.state === 'DEFENSIVE');
  });
  const dangerAlerts = alerts.filter((item: any) => cleanText(item?.level).toLowerCase() === 'danger');
  const urgentCount = dangerHoldings.length + dangerAlerts.length;
  const issueCount = issues.length;

  const focusStocks = themeStocks
    .map((stock: any) => ({
      ...stock,
      themeName: cleanText(stock?.themeName),
    }))
    .filter((stock: any) => cleanText(stock?.name ?? stock?.ticker))
    .sort((left: any, right: any) => {
      const riskGap = stockRiskPriorityScore(right) - stockRiskPriorityScore(left);
      if (riskGap !== 0) return riskGap;
      const rankGap = Number(left?.themeRank ?? 999) - Number(right?.themeRank ?? 999);
      if (rankGap !== 0) return rankGap;
      const valueGap = (nullableNumber(right?.tradingValue) ?? 0) - (nullableNumber(left?.tradingValue) ?? 0);
      if (valueGap !== 0) return valueGap;
      return Math.abs(nullableNumber(right?.changeRate) ?? 0) - Math.abs(nullableNumber(left?.changeRate) ?? 0);
    });

  const opportunityLead = opportunityCandidates.find((candidate: any) =>
    nullableNumber(candidate?.opportunityScore ?? candidate?.opportunity_score) !== null,
  ) ?? null;
  const focusStock = opportunityLead ?? focusStocks[0] ?? null;
  const focusStockName = cleanText(focusStock?.name ?? focusStock?.ticker);
  const focusRate = nullableNumber(focusStock?.changeRate ?? focusStock?.change_rate);
  const opportunityReason = opportunityLead
    ? cleanText(
        (Array.isArray(opportunityLead?.reasons) ? opportunityLead.reasons[0] : '')
        || opportunityLead?.category
        || opportunityLead?.label,
      )
    : '';
  const opportunityWarning = opportunityLead
    ? cleanText(Array.isArray(opportunityLead?.warnings) ? opportunityLead.warnings[0] : '')
    : '';
  const focusAction = opportunityLead
    ? {
        label: opportunityWarning || opportunityReason || '시장·테마·수급이 함께 정렬된 후보',
        tone: /추격|과열|위험|경계|주의/.test(opportunityWarning)
          ? 'warn' as Tone
          : 'up' as Tone,
      }
    : stockActionContext(focusStock, marketPreview);

  const marketPriority: PriorityItem = {
    order: 0,
    category: '시장',
    title: marketPreview.mode === 'prepare' ? '오늘 시장 마감 흐름' : marketStatus,
    description: marketPreview.description,
    action: marketPreview.mode === 'prepare' ? '오늘 수급 보기' : '시장 근거 확인',
    tab: marketPreview.tab,
    tone: currentMarketTone,
  };

  const themePriority: PriorityItem = {
    order: 0,
    category: '테마',
    title: leadThemeName === '테마 확인 중'
      ? leadThemeName
      : `${leadThemeName}${leadThemeChangeRate === null ? '' : ` ${formatPct(leadThemeChangeRate)}`}`,
    description: leadTheme
      ? '상승 테마의 확산 강도와 구성 종목의 외국인·기관·VWAP 흐름을 함께 확인합니다.'
      : '상승 테마 신호가 갱신되면 우선순위를 표시합니다.',
    action: '주도 테마 종목 확인',
    tab: 'THEME',
    tone: leadThemeChangeRate !== null && leadThemeChangeRate > 0 ? 'up' : 'neutral',
  };

  const stockPriority: PriorityItem = {
    order: 0,
    category: opportunityLead ? '기회 후보' : '주목 후보',
    title: focusStockName
      ? `${focusStockName}${focusRate === null ? '' : ` ${formatPct(focusRate)}`}`
      : '후보 신호 확인 중',
    description: focusStockName
      ? focusAction.label
      : '종목별 수급과 리스크 변화가 확인되면 우선순위를 표시합니다.',
    action: opportunityLead ? '기회 근거 확인' : '후보 확인',
    tab: 'STOCK',
    tone: focusAction.tone,
  };

  const holdingPriority: PriorityItem = {
    order: 0,
    category: '보유',
    title: urgentCount > 0 ? `즉시 확인할 보유 위험 ${urgentCount}건` : '보유종목 위험 변화 없음',
    description: urgentCount > 0
      ? '위험도·수급·가격 기준이 바뀐 보유종목부터 확인합니다.'
      : '현재 즉시 대응이 필요한 보유종목 변화는 없습니다.',
    action: '보유 판단 확인',
    tab: 'HOLDING',
    tone: urgentCount > 0 ? 'down' : 'good',
  };

  const priorities = [holdingPriority, marketPriority, themePriority, stockPriority]
    .sort((left, right) => {
      const toneRank: Record<Tone, number> = { down: 5, warn: 4, up: 3, good: 2, neutral: 1 };
      return toneRank[right.tone] - toneRank[left.tone];
    })
    .map((item, index) => ({...item, order: index + 1}));

  const metrics: CommandMetric[] = [
    {
      eyebrow: 'MARKET',
      label: '시장 상태',
      value: marketStatus,
      sub: marketPreview.mode === 'prepare'
        ? '장 시작 전 준비 모드'
        : marketPreview.badge || '시장 구조 요약',
      tone: currentMarketTone,
    },
    {
      eyebrow: 'THEME',
      label: '오늘 강한 테마',
      value: leadThemeName,
      sub: leadThemeChangeRate === null ? '테마 신호 확인 중' : formatPct(leadThemeChangeRate),
      tone: leadThemeChangeRate !== null && leadThemeChangeRate > 0 ? 'up' : 'neutral',
    },
    {
      eyebrow: 'ACTION',
      label: '내 종목 위험',
      value: `${urgentCount}건`,
      sub: urgentCount > 0 ? '보유 위험·긴급 알림' : '현재 즉시 위험 없음',
      tone: urgentCount > 0 ? 'down' : 'good',
    },
    {
      eyebrow: 'ISSUES',
      label: '오늘 이슈',
      value: `${issueCount}건`,
      sub: '정책·공시·시장 이슈',
      tone: 'neutral',
    },
  ];

  return {
    generatedAt: shortTime(raw?.generatedAt ?? raw?.refreshedAt ?? market?.generatedAt),
    marketStatus,
    leadThemeName,
    leadThemeChangeRate,
    issueCount,
    urgentCount,
    opportunityCount,
    watchCandidateCount,
    metrics,
    priorities,
    marketPreview,
  };
}






type AuthDashboardStock = {
  ticker: string;
  name: string;
  marketLabel: string;
  themeName: string;
  price: number | null;
  changeRate: number | null;
  tradingValue: number | null;
  foreignNetBuyAmount: number | null;
  institutionNetBuyAmount: number | null;
  smartMoneyNetBuyAmount: number | null;
  programNetBuyExecAmount: number | null;
  programNetBuyToTradingValuePct: number | null;
  vwapGapPct: number | null;
  opportunityScore: number | null;
  marketFitLabel: string;
  statusLabel: string;
  compactStatusLabel: string;
  tone: Tone;
  reason: string;
  isHolding: boolean;
  updatedAt: string;
  isRealtime: boolean;
  sourcePriority: number;
  actionDecision: NormalizedActionDecision;
  riskAssessment: NormalizedRiskAssessment;
  raw: any;
};

type AuthDashboardChange = {
  key: string;
  time: string;
  title: string;
  message: string;
  tone: Tone;
  entityKey: string;
  eventType: string;
  detectedAt: string;
  changeValue: number | null;
};

type AuthDashboardIssue = {
  key: string;
  title: string;
  summary: string;
  time: string;
  source: string;
  publishedAt: string;
};

type AuthDashboardModel = {
  generatedAt: string;
  marketStatus: string;
  marketTone: Tone;
  marketRiskScore: number | null;
  marketRiskLabel: string;
  marketDescription: string;
  marketAction: string;
  futuresLabel: string;
  futuresPrice: number | null;
  futuresChangeRate: number | null;
  marketBasis: number | null;
  basisLabel: string;
  oiChange5m: number | null;
  leadThemeName: string;
  leadThemeChangeRate: number | null;
  breadthPct: number | null;
  strongThemeCount: number;
  realtimeCoveragePct: number | null;
  stocks: AuthDashboardStock[];
  changes: AuthDashboardChange[];
  timelineChanges: AuthDashboardChange[];
  issues: AuthDashboardIssue[];
  alerts: Array<{ key: string; title: string; detail: string; tone: Tone }>;
  marketCheckpoints: string[];
};





function shortClock(value: unknown): string {
  const text = cleanText(value);
  if (!text) return '';
  const match = text.replace('T', ' ').match(/(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return text;
  return `${match[1]}:${match[2]}${match[3] ? `:${match[3]}` : ''}`;
}

function normalizedTone(value: unknown, fallbackText = ''): Tone {
  const raw = cleanText(value).toLowerCase();
  if (raw === 'down' || raw === 'danger' || raw === 'critical') return 'down';
  if (raw === 'warn' || raw === 'warning' || raw === 'watch') return 'warn';
  if (raw === 'good' || raw === 'safe') return 'good';
  if (raw === 'up' || raw === 'bull') return 'up';
  return toneFromText(`${cleanText(value)} ${fallbackText}`);
}

function compactRiskStateLabel(
  assessment: NormalizedRiskAssessment,
  decision: NormalizedActionDecision,
): string {
  const position = decision.positionType || assessment.positionType;
  if (position === 'WATCHLIST') {
    return userFacingEntryStage(assessment, decision).label;
  }

  if (assessment.available) {
    switch (assessment.state) {
      case 'HIGH_RISK':
        return '위험 높음';
      case 'DEFENSIVE':
        return '방어 필요';
      case 'DATA_LIMITED':
        return '데이터 확인';
      case 'WATCH':
        return '관찰 중';
      case 'RECOVERING':
        return '흐름 개선';
      case 'STABLE':
        return '안정';
      default:
        return assessment.stateLabel || '상태 확인';
    }
  }

  return decision.actionLabel || '데이터 확인';
}

function mergeDefinedStockFields(base: any, incoming: any): any {
  const out = { ...(base ?? {}) };
  if (!incoming || typeof incoming !== 'object') return out;

  Object.entries(incoming).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      out[key] = value;
    }
  });
  return out;
}


function buildAuthDashboardModel(
  response: LiveSummaryApiResponse,
  themeStocks: LiveSummaryThemeStock[] = [],
): AuthDashboardModel {
  const raw = response as any;
  const market = raw?.marketState ?? {};
  const context = raw?.marketContext ?? raw?.market_context ?? raw?.derivativeSignal ?? {};
  const themes = topRisingThemes(response);
  const holdings = Array.isArray(raw?.holdings) ? raw.holdings : [];
  const opportunitySource = raw?.opportunityRadar ?? raw?.opportunity_radar;
  const opportunities = Array.isArray(opportunitySource)
    ? opportunitySource
    : Array.isArray(opportunitySource?.candidates)
      ? opportunitySource.candidates
      : [];
  const changeEvents = Array.isArray(raw?.changeEvents) ? raw.changeEvents : [];
  const alertRows = Array.isArray(raw?.alerts) ? raw.alerts : [];
  const issueRowsCandidate = raw?.issues ?? raw?.realtimeIssues ?? raw?.realtime_issues;
  const issueRows = Array.isArray(issueRowsCandidate) ? issueRowsCandidate : [];

  const marketRiskScore = nullableNumber(
    context?.riskRealtimeScore
      ?? context?.risk_realtime_score
      ?? context?.entryRiskScore
      ?? context?.entry_risk_score,
  );
  const marketRiskLabel = cleanText(context?.riskLabel ?? context?.risk_label)
    || (marketRiskScore === null
      ? '확인 중'
      : marketRiskScore >= 70
        ? '높음'
        : marketRiskScore >= 45
          ? '보통'
          : '낮음');
  const marketTone: Tone = marketRiskScore !== null
    ? marketRiskScore >= 70
      ? 'down'
      : marketRiskScore >= 45
        ? 'warn'
        : 'good'
    : marketToneFromContext(market, context);

  const derivatives = context?.derivatives ?? {};
  const derivative = derivatives?.KOSPI200
    ?? derivatives?.KOSDAQ150
    ?? raw?.derivativeSignal
    ?? {};
  const futuresLabel = cleanText(derivative?.label ?? derivative?.contractName) || 'KOSPI200 선물';
  const futuresPrice = nullableNumber(derivative?.futuresPrice ?? derivative?.last);
  const futuresChangeRate = nullableNumber(derivative?.futuresChangeRate ?? derivative?.changeRate);
  const marketBasis = nullableNumber(derivative?.marketBasis);
  const oiChange5m = nullableNumber(derivative?.oiChange5m);
  const basisLabel = cleanText(
    derivative?.structureLabel
      ?? context?.basisFlowLabel
      ?? context?.basis_flow_label,
  ) || (marketBasis === null ? '확인 중' : marketBasis < 0 ? '약세' : marketBasis > 0 ? '우호' : '중립');

  const leadTheme = themes[0] ?? {};
  const leadThemeName = cleanText(leadTheme?.themeName ?? leadTheme?.theme_name) || '테마 확인 중';
  const leadThemeChangeRate = nullableNumber(leadTheme?.avgChangeRate ?? leadTheme?.avg_change_rate);
  const breadthPct = nullableNumber(leadTheme?.smartMoneyBreadthPct ?? leadTheme?.smart_money_breadth_pct);
  const realtimeCoveragePct = nullableNumber(leadTheme?.priceCoveragePct ?? leadTheme?.price_coverage_pct);

  const byTicker = new Map<string, AuthDashboardStock>();

  const addStock = (
    stock: any,
    meta: {
      isHolding: boolean;
      sourcePriority: number;
      themeName?: string;
      marketLabel?: string;
    },
  ) => {
    const ticker = cleanText(stock?.ticker ?? stock?.code);
    const name = cleanText(stock?.name ?? stock?.corpName ?? stock?.corp_name);
    const key = ticker || name;
    if (!key) return;

    const existing = byTicker.get(key);
    const isHolding = Boolean(existing?.isHolding || meta.isHolding);
    const merged = mergeDefinedStockFields(existing?.raw, stock);
    const incomingOwnsAuthority = !existing || meta.sourcePriority >= existing.sourcePriority;
    const expectedPosition = isHolding ? 'HOLDING' : 'WATCHLIST';

    // 판단 authority는 출처 우선순위를 따른다.
    // HOLDING > OPPORTUNITY > THEME이며, 하위 출처가 상위 Action/Risk 판단을 덮지 못한다.
    const actionDecision = incomingOwnsAuthority
      ? normalizeActionDecision(stock?.actionDecision, expectedPosition)
      : existing.actionDecision;
    const riskAssessment = incomingOwnsAuthority
      ? normalizeRiskAssessment(stock?.riskAssessment, expectedPosition)
      : existing.riskAssessment;
    const sourcePriority = Math.max(existing?.sourcePriority ?? 0, meta.sourcePriority);

    const fallbackReason = cleanText(
      merged?.warning
        ?? merged?.primaryReason
        ?? (Array.isArray(merged?.reasons) ? merged.reasons[0] : '')
        ?? merged?.flowReason
        ?? merged?.flowDecision,
    );
    const reason = actionDecision.headline
      || riskAssessment.summary
      || fallbackReason
      || '현재 판단에 필요한 데이터를 확인 중입니다.';
    const entryStage = userFacingEntryStage(riskAssessment, actionDecision);
    const statusLabel = isHolding
      ? (riskAssessment.available ? riskAssessment.stateLabel : actionDecision.actionLabel || '데이터 확인 중')
      : entryStage.label;
    const tone: Tone = isHolding
      ? (riskAssessment.available
        ? riskAssessment.tone
        : actionDecision.available
          ? actionDecision.tone
          : 'neutral')
      : entryStage.tone;

    byTicker.set(key, {
      ticker: ticker || existing?.ticker || '',
      name: name || existing?.name || ticker || '종목',
      marketLabel: cleanText(merged?.marketLabel ?? meta.marketLabel ?? existing?.marketLabel),
      themeName: cleanText(merged?.themeName ?? merged?.theme_name ?? meta.themeName ?? existing?.themeName),
      price: nullableNumber(merged?.price),
      changeRate: nullableNumber(merged?.changeRate ?? merged?.change_rate),
      tradingValue: nullableNumber(merged?.tradingValue ?? merged?.trading_value),
      foreignNetBuyAmount: nullableNumber(merged?.foreignNetBuyAmount ?? merged?.foreign_net_buy_amount),
      institutionNetBuyAmount: nullableNumber(merged?.institutionNetBuyAmount ?? merged?.institution_net_buy_amount),
      smartMoneyNetBuyAmount: nullableNumber(merged?.smartMoneyNetBuyAmount ?? merged?.smart_money_net_buy_amount),
      programNetBuyExecAmount: nullableNumber(merged?.programNetBuyExecAmount ?? merged?.program_net_buy_exec_amount),
      programNetBuyToTradingValuePct: nullableNumber(
        merged?.programNetBuyToTradingValuePct ?? merged?.program_net_buy_to_trading_value_pct,
      ),
      vwapGapPct: nullableNumber(merged?.vwapGapPct ?? merged?.vwap_gap_pct),
      opportunityScore: nullableNumber(merged?.opportunityScore ?? merged?.opportunity_score),
      marketFitLabel: cleanText(merged?.marketFitLabel ?? merged?.market_fit_label),
      statusLabel,
      compactStatusLabel: compactRiskStateLabel(riskAssessment, actionDecision),
      tone,
      reason,
      isHolding,
      updatedAt: shortClock(
        merged?.priceAsOf
          ?? merged?.lastEventAt
          ?? merged?.investorTrendUpdatedAt
          ?? merged?.programReceivedAt
          ?? merged?.investorReceivedAt,
      ),
      isRealtime: merged?.isRealtime === true || Number(merged?.isRealtime) === 1,
      sourcePriority,
      actionDecision,
      riskAssessment,
      raw: merged,
    });
  };

  // 낮은 authority부터 보충하고 마지막에 보유 판단을 고정한다.
  // TOP 10 상승 테마의 매핑 종목을 기본 시장 후보 universe로 사용한다.
  themeStocks.forEach((stock: any) => {
    addStock(stock, {
      isHolding: false,
      sourcePriority: 10,
      themeName: cleanText(stock?.themeName ?? stock?.theme_name),
      marketLabel: cleanText(stock?.market),
    });
  });
  opportunities.forEach((stock: any) => addStock(stock, {
    isHolding: false,
    sourcePriority: 20,
    themeName: cleanText(stock?.themeName ?? stock?.theme_name ?? stock?.sectorName),
    marketLabel: cleanText(stock?.marketLabel),
  }));
  holdings.forEach((stock: any) => addStock(stock, {
    isHolding: true,
    sourcePriority: 30,
  }));

  const stocks: AuthDashboardStock[] = Array.from(byTicker.values())
    .sort((left: AuthDashboardStock, right: AuthDashboardStock) => {
      if (left.isHolding !== right.isHolding) return left.isHolding ? -1 : 1;

      if (left.isHolding && right.isHolding) {
        const riskGap = riskAssessmentPriority(right.riskAssessment)
          - riskAssessmentPriority(left.riskAssessment);
        if (riskGap !== 0) return riskGap;
      }

      const scoreGap = (right.opportunityScore ?? -1) - (left.opportunityScore ?? -1);
      if (scoreGap !== 0) return scoreGap;

      const readinessGap = right.actionDecision.evidenceReadyCount
        - left.actionDecision.evidenceReadyCount;
      if (readinessGap !== 0) return readinessGap;

      return Math.abs(right.changeRate ?? 0) - Math.abs(left.changeRate ?? 0);
    })
    .slice(0, 8);

  const timelineChanges: AuthDashboardChange[] = changeEvents
    .map((event: any, index: number) => {
      const title = cleanText(event?.title ?? event?.eventType ?? event?.event_type);
      const message = cleanText(event?.message ?? event?.currentValue ?? event?.current_value);
      const severity = cleanText(event?.severity);
      const detectedAt = cleanText(
        event?.detectedAt
          ?? event?.sourceEventAt
          ?? event?.detected_at
          ?? event?.source_event_at,
      );
      const eventType = cleanText(event?.eventType ?? event?.event_type).toUpperCase();

      return {
        key: cleanText(event?.id) || `${index}-${eventType || title}`,
        time: shortClock(detectedAt),
        title: title || message || '변화 감지',
        message,
        tone: normalizedTone(severity, `${title} ${message}`),
        entityKey: cleanText(event?.entityKey ?? event?.entity_key),
        eventType,
        detectedAt,
        changeValue: nullableNumber(event?.changeValue ?? event?.change_value),
      };
    })
    .filter((event: AuthDashboardChange) => seoulDateKey(event.detectedAt) === seoulDateKey(new Date()))
    .slice(0, 50);

  const changeSemanticKeys = new Set<string>();
  const changes: AuthDashboardChange[] = [];

  timelineChanges.forEach((event) => {
    const normalizedGenericType = /^(CHANGE|UPDATE|STATE_CHANGE|STATUS_CHANGE)$/.test(event.eventType)
      ? ''
      : event.eventType;
    const fallbackSemanticType = /보유 상태/.test(event.title)
      ? 'HOLDING_STATE'
      : /프로그램/.test(event.title)
        ? 'PROGRAM_FLOW'
        : /외국인|기관|수급/.test(event.title)
          ? 'INVESTOR_FLOW'
          : /위험|리스크/.test(event.title)
            ? 'RISK_STATE'
            : /선물|베이시스/.test(event.title)
              ? 'DERIVATIVE_STATE'
              : event.title;
    const semanticType = normalizedGenericType || fallbackSemanticType;
    const semanticKey = `${event.entityKey || 'MARKET'}|${semanticType}`;

    if (changeSemanticKeys.has(semanticKey)) return;
    changeSemanticKeys.add(semanticKey);
    changes.push(event);
  });

  const issueKeys = new Set<string>();
  const issues: AuthDashboardIssue[] = issueRows
    .map((issue: any, index: number) => {
      const title = cleanText(issue?.title ?? issue?.headline ?? issue?.name);
      const summary = cleanText(issue?.summary ?? issue?.description ?? issue?.message);
      const publishedAt = cleanText(
        issue?.publishedAt
          ?? issue?.published_at
          ?? issue?.detectedAt
          ?? issue?.createdAt
          ?? issue?.created_at,
      );
      const source = cleanText(issue?.source ?? issue?.provider ?? issue?.publisher);

      return {
        key: cleanText(issue?.id) || `issue-${index}-${title}`,
        title,
        summary,
        time: shortClock(publishedAt),
        source,
        publishedAt,
      };
    })
    .filter((issue) => Boolean(issue.title))
    .filter((issue) => {
      const dateKey = seoulDateKey(issue.publishedAt);
      return !dateKey || dateKey === seoulDateKey(new Date());
    })
    .filter((issue) => {
      const semanticKey = issue.title
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/[^0-9a-z가-힣 ]/g, '')
        .trim();

      if (!semanticKey || issueKeys.has(semanticKey)) return false;
      issueKeys.add(semanticKey);
      return true;
    })
    .slice(0, 5);

  const alertItems = alertRows
    .map((alert: any, index: number) => {
      const message = cleanText(alert?.message);
      return {
        key: `alert-${index}-${message}`,
        title: message || '알림',
        detail: cleanText(alert?.level),
        tone: normalizedTone(alert?.level, message),
      };
    })
    .filter((item: { title: string }) => Boolean(item.title));

  const stockWarnings = stocks
    .filter((stock) => stock.riskAssessment.available
      && ['HIGH_RISK', 'DEFENSIVE', 'UNFAVORABLE'].includes(stock.riskAssessment.state))
    .map((stock) => ({
      key: `stock-${stock.ticker || stock.name}`,
      title: stock.name,
      detail: `${stock.compactStatusLabel} · ${stock.reason}`,
      tone: stock.tone,
    }));

  const alerts = [...stockWarnings, ...alertItems].slice(0, 3);

  const marketCheckpoints = Array.isArray(context?.checkpoints)
    ? context.checkpoints.map((value: unknown) => cleanText(value)).filter(Boolean).slice(0, 4)
    : [];

  return {
    generatedAt: shortClock(raw?.generatedAt ?? raw?.refreshedAt ?? market?.generatedAt),
    marketStatus: marketStatusLabel(market?.label, context),
    marketTone,
    marketRiskScore,
    marketRiskLabel,
    marketDescription: cleanText(
      context?.description
        ?? context?.summary
        ?? context?.comment
        ?? market?.comment,
    ) || '시장 구조와 수급 변화를 확인하고 있습니다.',
    marketAction: cleanText(
      context?.action
        ?? context?.actionLabel
        ?? context?.actionGuide
        ?? context?.transitionDescription,
    ) || '시장·테마·수급이 함께 정렬되는지 확인하세요.',
    futuresLabel,
    futuresPrice,
    futuresChangeRate,
    marketBasis,
    basisLabel,
    oiChange5m,
    leadThemeName,
    leadThemeChangeRate,
    breadthPct,
    strongThemeCount: themes.length,
    realtimeCoveragePct,
    stocks,
    changes: changes.slice(0, 12),
    timelineChanges,
    issues,
    alerts,
    marketCheckpoints,
  };
}

function marketToneFromContext(market: any, context: any): Tone {
  const source = `${cleanText(context?.tone)} ${cleanText(context?.headline)} ${cleanText(context?.riskLabel)} ${cleanText(market?.tone)} ${cleanText(market?.label)}`;
  return normalizedTone(source);
}



// POLICYINTEL_P1_2_HOME_CHANGES_V2_20261010


function homeChangeLayerPriority(change: LiveSummaryChangeItem): number {
  const layer = cleanText(change.changeLayer).toUpperCase();
  if (layer === 'JUDGMENT_CHANGE') return 0;
  if (layer === 'INTERPRETATION_CHANGE') return 1;
  if (layer === 'FACT_CHANGE') return 2;
  return 3;
}



function seoulDateKey(value: unknown): string {
  const text = cleanText(value);
  const direct = text.match(/(\d{4}-\d{2}-\d{2})/);
  if (direct) return direct[1];

  const parsed = text ? new Date(text) : new Date();
  if (Number.isNaN(parsed.getTime())) return '';

  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(parsed);
}















const MAIN_PENDING_ACTION_KEY = 'policy-intel:main-pending-action';
const MAIN_PENDING_ACTION_MAX_AGE_MS = 30 * 60 * 1000;

type MainPendingAction =
  | { type: 'SCANNER'; presetCode: string; createdAt: number };


function consumeMainPendingAction(): MainPendingAction | null {
  try {
    const raw = sessionStorage.getItem(MAIN_PENDING_ACTION_KEY);
    sessionStorage.removeItem(MAIN_PENDING_ACTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<MainPendingAction> & { createdAt?: number };
    const createdAt = Number(parsed.createdAt || 0);
    if (!createdAt || Date.now() - createdAt > MAIN_PENDING_ACTION_MAX_AGE_MS) return null;
    if (parsed.type === 'SCANNER' && cleanText((parsed as any).presetCode)) {
      return {
        type: 'SCANNER',
        presetCode: cleanText((parsed as any).presetCode),
        createdAt,
      };
    }
    return null;
  } catch {
    return null;
  }
}























































// POLICYINTEL_MAIN_EDITORIAL_V6_20261025
// Evidence-first, data-dense presentation. Existing API/auth/data authority unchanged.




export default function MainPage() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (new URLSearchParams(location.search).get('focus') !== 'stock-search') return;
    const raf = window.requestAnimationFrame(() => document.getElementById('pi2-stock-input')?.focus());
    return () => window.cancelAnimationFrame(raf);
  }, [location.search]);
  const authState = useAppSelector((state) => state.auth);
  const user = authState.user;
  const retentionIdentity = cleanText(user?.email);
  const authResolved = Boolean(
    authState.authInitialized
      && authState.authStatus !== 'AUTH_CHECKING',
  );
  const isAuthenticated = Boolean(
    authResolved
      && authState.authStatus === 'AUTHENTICATED'
      && authState.isAuthenticated
      && user?.email,
  );
  const [liveSummary, setLiveSummary] = useState<LiveSummaryApiResponse | null>(null);
  const [themeStocks, setThemeStocks] = useState<LiveSummaryThemeStock[]>([]);
  const themeStocksAbortRef = useRef<AbortController | null>(null);
  const [loading, setLoading] = useState(false);
  const [, setLoadError] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  const [homeChanges, setHomeChanges] = useState<LiveSummaryChangesResponse | null>(null);
  const [, setHomeChangesLoading] = useState(false);
  const [, setHomeChangesError] = useState('');
  const homeChangesAbortRef = useRef<AbortController | null>(null);
  const [guestStockQuery, setGuestStockQuery] = useState('');
  const [guestStockSuggestions, setGuestStockSuggestions] = useState<StockSearchSuggestion[]>([]);
  const [guestValuePreview, setGuestValuePreview] = useState<any>(null);
  const [guestValuePreviewLoading, setGuestValuePreviewLoading] = useState(false);
  const [guestValuePreviewError, setGuestValuePreviewError] = useState('');
  const [, setGuestStockSearchLoading] = useState(false);
  const guestStockSearchSeqRef = useRef(0);
  const guestStockPreviewSeqRef = useRef(0);
  const guestStockSubmitSeqRef = useRef(0);
  const [showMarketDetails, ] = useState(false);
  const [, ] = useState(false);
  const [, setMarketIntraday] = useState<MarketIntradayResponse | null>(null);
  const [, setMarketIntradayLoading] = useState(false);
  const [, setMarketIntradayError] = useState('');
  const marketIntradayRequestSeqRef = useRef(0);
  const [, setOffhoursProxy] = useState<OffhoursProxyResponse | null>(null);
  const [, setOffhoursProxyHistory] = useState<OffhoursProxyHistoryResponse | null>(null);
  const [, setOffhoursProxyLoading] = useState(false);
  const [, setOffhoursProxyError] = useState('');
  const offhoursProxyRequestSeqRef = useRef(0);
  const engagementMilestonesRef = useRef<Set<string>>(new Set());
  const guestConversionViewRef = useRef<Set<string>>(new Set());
  const guestTrackIntentRef = useRef<Map<string, string>>(new Map());
  const getGuestTrackIntentId = useCallback((ticker: string) => {
    const normalizedTicker = cleanText(ticker);
    const existing = guestTrackIntentRef.current.get(normalizedTicker);
    if (existing) return existing;
    const next = createClientIntentId(`guest-track-${normalizedTicker || 'unknown'}`);
    guestTrackIntentRef.current.set(normalizedTicker, next);
    return next;
  }, []);
  const guestPriceCardRef = useRef<HTMLDivElement | null>(null);
  const guestValueCardRef = useRef<HTMLDivElement | null>(null);
  const guestReasonCardRef = useRef<HTMLDivElement | null>(null);
  const guestTrackCtaButtonRef = useRef<HTMLButtonElement | null>(null);
  const guestConditionCardRef = useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    // App.tsx의 trackFunnelLandingView가 landing_view의 단일 authority다.
    // MainPage에서 landing_view를 중복 발행하면 같은 세션이 재마운트될 때
    // 신규 방문처럼 여러 번 보일 수 있으므로 여기서는 제거한다.
    if (!authResolved) return;

    trackPageView(
      isAuthenticated ? 'main_daily_command_board' : 'main_daily_command_board_guest',
      { authenticated: isAuthenticated },
    );

    if (!isAuthenticated) {
      try {
        const host = document.referrer ? new URL(document.referrer).hostname.toLowerCase() : '';
        const googleOrganic = host === 'google.com' || host === 'www.google.com' || host.startsWith('google.') || host.includes('.google.');
        const organicKey = 'policy-intel:organic-landing:google:v1';
        const alreadyTracked = sessionStorage.getItem(organicKey) === '1';
        if (googleOrganic && !alreadyTracked) {
          sessionStorage.setItem(organicKey, '1');
          trackUxEvent('organic_landing', {
            source: 'google-organic-main',
            authenticated: false,
            funnel_version: 'organic-v2',
          });
        }
      } catch {
        // referrer/storage가 제한되어도 메인 UX는 계속한다.
      }
    } else {
      emitTrackedReturnMilestones('main', retentionIdentity);
    }
  }, [authResolved, isAuthenticated, retentionIdentity]);

  useEffect(() => {
    if (!authResolved) return undefined;

    engagementMilestonesRef.current = new Set();
    const screenName = isAuthenticated ? 'main_daily_command_board' : 'main_daily_command_board_guest';

    const emitMilestone = (milestone: string, extra: Record<string, unknown> = {}) => {
      if (engagementMilestonesRef.current.has(milestone)) return;
      engagementMilestonesRef.current.add(milestone);
      const payload = {screen_name: screenName, authenticated: isAuthenticated, milestone, ...extra};
      trackEvent('main_engagement_milestone', payload);
      trackUxEvent('main_engagement_milestone', payload);
    };

    const timer10 = window.setTimeout(() => emitMilestone('stay_10s'), 10_000);
    const timer30 = window.setTimeout(() => emitMilestone('stay_30s'), 30_000);
    const onScroll = () => {
      const doc = document.documentElement;
      const maxScroll = Math.max(1, doc.scrollHeight - window.innerHeight);
      const ratio = Math.max(0, Math.min(1, window.scrollY / maxScroll));
      if (ratio >= 0.5) emitMilestone('scroll_50', {scroll_ratio: Number(ratio.toFixed(2))});
      if (ratio >= 0.8) emitMilestone('scroll_80', {scroll_ratio: Number(ratio.toFixed(2))});
    };
    window.addEventListener('scroll', onScroll, {passive: true});

    return () => {
      window.clearTimeout(timer10);
      window.clearTimeout(timer30);
      window.removeEventListener('scroll', onScroll);
    };
  }, [authResolved, isAuthenticated]);

  const loadCommandBoard = useCallback(async (force = false) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setLoadError('');

    try {
      const response = await getLiveSummary({
        phase: 'AUTO',
        timeoutMs: 8_000,
        refresh: isAuthenticated ? force : false,
        includeIssues: isAuthenticated,
        signal: controller.signal,
      });
      if (!controller.signal.aborted) {
        setLiveSummary(response);
      }
    } catch (error) {
      const name = (error as { name?: string })?.name;
      if (name !== 'CanceledError' && name !== 'AbortError' && !controller.signal.aborted) {
        setLoadError('오늘 브리핑을 불러오지 못했습니다. Action Desk에서 다시 확인해 주세요.');
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void loadCommandBoard(false);

    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void loadCommandBoard(false);
    }, MAIN_AUTO_REFRESH_MS);

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      void loadCommandBoard(false);
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      abortRef.current?.abort();
    };
  }, [loadCommandBoard]);

  const loadHomeChanges = useCallback(async () => {
    if (!isAuthenticated) {
      homeChangesAbortRef.current?.abort();
      setHomeChanges(null);
      setHomeChangesError('');
      setHomeChangesLoading(false);
      return;
    }

    homeChangesAbortRef.current?.abort();

    const controller = new AbortController();
    homeChangesAbortRef.current = controller;

    setHomeChangesLoading(true);
    setHomeChangesError('');

    try {
      const response = await getLiveSummaryChanges({
        limit: 100,
        timeoutMs: 5_000,
        signal: controller.signal,
      });

      if (!controller.signal.aborted) {
        setHomeChanges(response);
      }
    } catch (error) {
      const name = (error as { name?: string })?.name;

      if (
        name !== 'CanceledError'
        && name !== 'AbortError'
        && !controller.signal.aborted
      ) {
        setHomeChangesError('어제 이후 변화 내용을 불러오지 못했습니다.');
      }
    } finally {
      if (!controller.signal.aborted) {
        setHomeChangesLoading(false);
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      homeChangesAbortRef.current?.abort();
      setHomeChanges(null);
      setHomeChangesError('');
      setHomeChangesLoading(false);
      return undefined;
    }

    void loadHomeChanges();

    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void loadHomeChanges();
    }, MAIN_AUTO_REFRESH_MS);

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      void loadHomeChanges();
    };

    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      homeChangesAbortRef.current?.abort();
    };
  }, [isAuthenticated, loadHomeChanges]);

  const loadMarketIntraday = useCallback(async () => {
    if (!isAuthenticated) {
      marketIntradayRequestSeqRef.current += 1;
      setMarketIntraday(null);
      setMarketIntradayLoading(false);
      setMarketIntradayError('');
      return;
    }

    const requestSeq = marketIntradayRequestSeqRef.current + 1;
    marketIntradayRequestSeqRef.current = requestSeq;
    setMarketIntradayLoading(true);
    setMarketIntradayError('');

    try {
      const response = await getMarketIntraday();
      if (marketIntradayRequestSeqRef.current === requestSeq) {
        setMarketIntraday(response);
      }
    } catch {
      if (marketIntradayRequestSeqRef.current === requestSeq) {
        setMarketIntradayError('KIS 당일 시장선을 불러오지 못했습니다.');
      }
    } finally {
      if (marketIntradayRequestSeqRef.current === requestSeq) {
        setMarketIntradayLoading(false);
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !showMarketDetails) {
      marketIntradayRequestSeqRef.current += 1;
      setMarketIntraday(null);
      setMarketIntradayLoading(false);
      setMarketIntradayError('');
      return undefined;
    }

    void loadMarketIntraday();

    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void loadMarketIntraday();
    }, MAIN_AUTO_REFRESH_MS);

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      void loadMarketIntraday();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      marketIntradayRequestSeqRef.current += 1;
    };
  }, [isAuthenticated, showMarketDetails, loadMarketIntraday]);

  const loadOffhoursProxy = useCallback(async () => {
    if (!isAuthenticated) {
      offhoursProxyRequestSeqRef.current += 1;
      setOffhoursProxy(null);
      setOffhoursProxyHistory(null);
      setOffhoursProxyLoading(false);
      setOffhoursProxyError('');
      return;
    }

    const requestSeq = offhoursProxyRequestSeqRef.current + 1;
    offhoursProxyRequestSeqRef.current = requestSeq;
    setOffhoursProxyLoading(true);
    setOffhoursProxyError('');

    try {
      const response = await getOffhoursMarketProxy();
      if (offhoursProxyRequestSeqRef.current !== requestSeq) return;

      // Main session authority는 기존 latest endpoint가 그대로 결정한다.
      setOffhoursProxy(response);

      const active = response?.mode === 'ACTIVE'
        && response?.displayEligible === true
        && Array.isArray(response?.items)
        && response.items.length > 0;

      if (!active) {
        setOffhoursProxyHistory(null);
        return;
      }

      try {
        const history = await getOffhoursMarketProxyHistory();
        if (offhoursProxyRequestSeqRef.current === requestSeq) {
          setOffhoursProxyHistory(
            history?.mode === 'ACTIVE' && history?.displayEligible === true
              ? history
              : null,
          );
        }
      } catch {
        if (offhoursProxyRequestSeqRef.current === requestSeq) {
          // latest authority는 유지하되, history 실패를 가짜 chart로 보정하지 않는다.
          setOffhoursProxyHistory(null);
          setOffhoursProxyError('24H 프록시 최신값은 정상이나 1·3·5·10·30분 차트 이력을 확인하지 못했습니다.');
        }
      }
    } catch {
      if (offhoursProxyRequestSeqRef.current === requestSeq) {
        setOffhoursProxy(null);
        setOffhoursProxyHistory(null);
        setOffhoursProxyError('공식시장 공백 보조 데이터를 확인하지 못했습니다.');
      }
    } finally {
      if (offhoursProxyRequestSeqRef.current === requestSeq) {
        setOffhoursProxyLoading(false);
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !showMarketDetails) {
      offhoursProxyRequestSeqRef.current += 1;
      setOffhoursProxy(null);
      setOffhoursProxyHistory(null);
      setOffhoursProxyLoading(false);
      setOffhoursProxyError('');
      return undefined;
    }

    void loadOffhoursProxy();

    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void loadOffhoursProxy();
    }, MAIN_AUTO_REFRESH_MS);

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      void loadOffhoursProxy();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      offhoursProxyRequestSeqRef.current += 1;
    };
  }, [isAuthenticated, showMarketDetails, loadOffhoursProxy]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const pending = consumeMainPendingAction();
    if (!pending) return;

    const openPanelAfterAction = (tab: 'HOLDINGS' | 'SCANNER', detail?: Record<string, unknown>) => {
      window.setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('policy-intel:live-summary-open', {
            detail: { tab, refresh: true, source: 'main-post-login-action', ...detail },
          }),
        );
      }, 300);
    };


    void (async () => {
      try {
        const dashboard = await getScannerDashboard();
        const targetRule = dashboard.rules.find((rule) =>
          Boolean(rule.managed)
          && cleanText(rule.presetCode).toUpperCase() === pending.presetCode.toUpperCase(),
        );
        if (targetRule && !targetRule.enabled) {
          await setScannerRuleEnabled(targetRule.id, true);
        }
        try {
          sessionStorage.setItem('policy-intel:scanner-focus-preset', pending.presetCode);
        } catch {
          // 선택 preset 힌트 저장 실패는 스캐너 진입을 막지 않는다.
        }
        trackUxEvent('main_guest_scanner_continue_success', {
          presetCode: pending.presetCode,
          ruleFound: Boolean(targetRule),
          source: 'main-post-login',
        });
        openPanelAfterAction('SCANNER', { presetCode: pending.presetCode });
      } catch (error) {
        trackUxEvent('main_guest_scanner_continue_failed', {
          presetCode: pending.presetCode,
          status: Number((error as any)?.response?.status ?? 0) || null,
          source: 'main-post-login',
        });
        openPanelAfterAction('SCANNER', { presetCode: pending.presetCode });
      }
    })();
  }, [isAuthenticated]);

  const topThemes = useMemo(() => topRisingThemes(liveSummary), [liveSummary]);
  const topThemeSignature = useMemo(
    () => topThemes
      .map((theme: any) => cleanText(theme?.themeCode ?? theme?.theme_code))
      .filter(Boolean)
      .join(','),
    [topThemes],
  );
  const topThemeFetchKey = `${topThemeSignature}|${cleanText((liveSummary as any)?.generatedAt)}`;

  useEffect(() => {
    themeStocksAbortRef.current?.abort();

    if (!isAuthenticated || !topThemes.length) {
      setThemeStocks([]);
      return undefined;
    }

    const controller = new AbortController();
    themeStocksAbortRef.current = controller;

    void Promise.all(
      topThemes.map(async (theme: any) => {
        const themeCode = cleanText(theme?.themeCode ?? theme?.theme_code);
        if (!themeCode) return {theme, rows: [] as LiveSummaryThemeStock[]};
        try {
          const rows = await getLiveSummaryThemeStocks(themeCode, {
            limit: THEME_STOCK_FETCH_LIMIT,
            timeoutMs: 8_000,
            signal: controller.signal,
          });
          return {theme, rows};
        } catch (error) {
          const name = (error as {name?: string})?.name;
          if (name === 'CanceledError' || name === 'AbortError' || controller.signal.aborted) {
            return {theme, rows: [] as LiveSummaryThemeStock[]};
          }
          return {theme, rows: [] as LiveSummaryThemeStock[]};
        }
      }),
    ).then((rowsByTheme) => {
      if (controller.signal.aborted) return;
      setThemeStocks(mergeTopThemeStocks(topThemes, rowsByTheme));
    });

    return () => controller.abort();
  // MainPage 자체 갱신 주기(60초)에 맞춰 TOP 10 테마 구성종목 스냅샷을 다시 읽는다.
  // collector의 WS 실시간성 자체는 backend authority이며, 브라우저는 종목별 polling 대신 테마별 snapshot만 읽는다.
  // 후속 backend batch endpoint가 생기면 이 Promise.all만 단일 호출로 교체한다.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, topThemeFetchKey]);


useMemo(
    () => (liveSummary ? buildCommandBoard(liveSummary, themeStocks) : null),
    [liveSummary, themeStocks],
  );

  const authDashboard = useMemo(
    () => (liveSummary ? buildAuthDashboardModel(liveSummary, themeStocks) : null),
    [liveSummary, themeStocks],
  );
  const [selectedTicker, setSelectedTicker] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      if (selectedTicker) setSelectedTicker('');
      return;
    }

    const rows = authDashboard?.stocks ?? [];
    if (!rows.length) return;

    if (!selectedTicker || !rows.some((row) => row.ticker === selectedTicker)) {
      setSelectedTicker(rows[0].ticker);
    }
  }, [isAuthenticated, authDashboard, selectedTicker]);

  const trackMainInteraction = useCallback((eventName: string, payload: Record<string, unknown> = {}) => {
    const eventPayload = {
      screen_name: isAuthenticated ? 'main_daily_command_board' : 'main_daily_command_board_guest',
      authenticated: isAuthenticated,
      ...payload,
    };
    trackEvent(eventName, eventPayload);
    trackUxEvent(eventName, eventPayload);
  }, [isAuthenticated]);

  const openActionDesk = useCallback((
    tab: LiveSummaryTab | 'ISSUES' = 'SUMMARY',
    source = 'main-command-board',
    target: { ticker?: string; issueId?: string; issueQuery?: string } = {},
  ) => {
    const analyticsPayload = {
      screen_name: isAuthenticated ? 'main_daily_command_board' : 'main_daily_command_board_guest',
      tab,
      source,
      authenticated: isAuthenticated,
      ticker: target.ticker || undefined,
      issue_id: target.issueId || undefined,
      issue_query: target.issueQuery || undefined,
    };

    // Main은 발견/요약까지만 담당하고 상세 상태는 LiveSummary panel authority로 넘긴다.
    trackEvent('main_action_desk_open_click', analyticsPayload);
    trackUxEvent('main_action_desk_open_click', analyticsPayload);

    window.dispatchEvent(
      new CustomEvent('policy-intel:live-summary-open', {
        detail: {
          tab,
          source,
          ticker: target.ticker,
          issueId: target.issueId,
          issueQuery: target.issueQuery,
          refresh: false,
          snapshot: liveSummary ?? undefined,
        },
      }),
    );
  }, [isAuthenticated, liveSummary]);


  useEffect(() => {
    if (isAuthenticated) {
      guestStockSearchSeqRef.current += 1;
      setGuestStockSuggestions([]);
      setGuestStockSearchLoading(false);
      return undefined;
    }

    const keyword = cleanText(guestStockQuery);
    if (!keyword) {
      guestStockSearchSeqRef.current += 1;
      setGuestStockSuggestions([]);
      setGuestStockSearchLoading(false);
      return undefined;
    }

    const requestSeq = guestStockSearchSeqRef.current + 1;
    guestStockSearchSeqRef.current = requestSeq;
    setGuestStockSearchLoading(true);

    const timer = window.setTimeout(() => {
      void searchStockSuggestions(keyword, 6)
        .then((rows) => {
          if (guestStockSearchSeqRef.current !== requestSeq) return;
          setGuestStockSuggestions(rows);
        })
        .catch(() => {
          if (guestStockSearchSeqRef.current !== requestSeq) return;
          setGuestStockSuggestions([]);
        })
        .finally(() => {
          if (guestStockSearchSeqRef.current === requestSeq) {
            setGuestStockSearchLoading(false);
          }
        });
    }, 220);

    return () => window.clearTimeout(timer);
  }, [guestStockQuery, isAuthenticated]);

  const openGuestStockPreview = useCallback((stock: StockSearchSuggestion) => {
    const ticker = cleanText(stock?.ticker);
    if (!/^\d{6}$/.test(ticker)) return;

    const requestSeq = ++guestStockPreviewSeqRef.current;
    guestStockSubmitSeqRef.current += 1; // 새 선택 시 오래된 검색 제출 응답 무효화
    const requestAt = performance.now();
    trackMainInteraction('stock_search', { ticker, stock_name: stock.corpName, source: 'main-guest-search' });
    setGuestValuePreviewLoading(true);
    setGuestValuePreviewError('');

    void fetch(`/api/main/market-intelligence/stocks/${ticker}`, { credentials: 'include', headers: { Accept: 'application/json' } })
      .then((response) => { if (!response.ok) throw new Error(`HTTP ${response.status}`); return response.json(); })
      .then((next) => {
        if (requestSeq !== guestStockPreviewSeqRef.current) return;
        const returnedTicker = String(next?.stock?.ticker ?? next?.stock?.stockCode ?? next?.stock?.stock_code ?? '').replace(/^A/, '');
        if (returnedTicker !== ticker) throw new Error('MISMATCHED_STOCK_PREVIEW_TICKER');
        setGuestValuePreview(next);
        trackUxEvent('stock_check_view', { ticker, stock_code: ticker, source: 'main-guest-preview', result: 'success', first_answer_ms: Math.round(performance.now()-requestAt), funnel_version: 'ui-v2' });
        window.requestAnimationFrame(() => document.getElementById('pi2-stock-result')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        setGuestStockQuery(cleanText(next?.stock?.corpName ?? next?.stock?.corp_name ?? stock.corpName) || stock.corpName);
        setGuestStockSuggestions([]);
      })
      .catch(() => { if (requestSeq !== guestStockPreviewSeqRef.current) return; trackUxEvent('stock_check_failed',{ticker,stock_code:ticker,source:'main-guest-preview',first_answer_ms:Math.round(performance.now()-requestAt),funnel_version:'ui-v2'}); setGuestValuePreview(null); setGuestValuePreviewError('현재 분석 데이터를 불러오지 못했습니다. 다시 검색해 주세요.'); })
      .finally(() => { if (requestSeq === guestStockPreviewSeqRef.current) setGuestValuePreviewLoading(false); });
  }, [trackMainInteraction]);

  const submitGuestStockSearch = useCallback(() => {
    const keyword = cleanText(guestStockQuery);
    if (!keyword) {
      trackMainInteraction('main_guest_stock_search_submit', {has_query: false});
      return;
    }

    trackMainInteraction('main_guest_stock_search_submit', {
      has_query: true,
      query_length: keyword.length,
      result_count: guestStockSuggestions.length,
    });

    const exact = guestStockSuggestions.find((row) => row.ticker === keyword || row.corpName === keyword);
    const target = exact ?? guestStockSuggestions.find((row) => row.ticker.startsWith(keyword) || row.corpName.startsWith(keyword));
    if (target) {
      openGuestStockPreview(target);
      return;
    }

    // 자동완성 응답보다 Enter/버튼이 먼저 눌려도 검색이 죽지 않게
    // 동일한 stock-search authority에서 1건을 다시 확인한다.
    const submitSeq = ++guestStockSubmitSeqRef.current;
    setGuestValuePreviewLoading(true);
    setGuestValuePreviewError('');
    void searchStockSuggestions(keyword, 1)
      .then((rows) => {
        if (submitSeq !== guestStockSubmitSeqRef.current) return;
        const first = rows[0];
        if (!first) {
          setGuestValuePreview(null);
          setGuestValuePreviewError('종목을 찾지 못했습니다. 종목명 또는 6자리 코드를 다시 확인해 주세요.');
          setGuestValuePreviewLoading(false);
          return;
        }
        openGuestStockPreview(first);
      })
      .catch(() => {
        if (submitSeq !== guestStockSubmitSeqRef.current) return;
        setGuestValuePreview(null);
        setGuestValuePreviewError('종목 검색에 실패했습니다. 잠시 후 다시 확인해 주세요.');
        setGuestValuePreviewLoading(false);
      });
  }, [guestStockQuery, guestStockSuggestions, openGuestStockPreview, trackMainInteraction]);


  const handleGuestQueryChange = useCallback((nextValue: string) => {
    setGuestStockQuery(nextValue);
    const current = guestValuePreview?.stock;
    const currentName = cleanText(current?.corpName ?? current?.corp_name ?? current?.name);
    const currentTicker = cleanText(current?.ticker ?? current?.stockCode ?? current?.stock_code).replace(/^A/, '');
    const normalizedNext = cleanText(nextValue);
    if (!current || (normalizedNext !== currentName && normalizedNext !== currentTicker)) {
      guestStockPreviewSeqRef.current += 1;
      guestStockSubmitSeqRef.current += 1;
      setGuestValuePreview(null);
      setGuestValuePreviewError('');
      setGuestValuePreviewLoading(false);
    }
  }, [guestValuePreview]);

  const requireLoginForGuestEvidence = useCallback((ticker: string, name: string) => {
    if (!/^\d{6}$/.test(ticker)) return;

    trackUxEvent('guest_evidence_login_click', {
      ticker,
      stock_code: ticker,
      stock_name: name,
      source: 'main-guest-evidence',
      return_to: `/stocks/${ticker}`,
      funnel_version: 'organic-v2',
    });
    trackUxEvent('auth_start', {
      ticker,
      stock_code: ticker,
      stock_name: name,
      source: 'main-guest-evidence',
      intent: 'EVIDENCE_DETAIL',
      return_to: `/stocks/${ticker}`,
      funnel_version: 'organic-v2',
    });
    window.dispatchEvent(
      new CustomEvent('policy-intel:require-auth', {
        detail: {
          returnTo: `/stocks/${ticker}`,
          panelTab: 'SUMMARY',
          openPanel: false,
          source: 'main-guest-evidence',
          intent: 'EVIDENCE_DETAIL',
          stockCode: ticker,
          stockName: name,
        },
      }),
    );
  }, []);

  const requireLoginForWatchlist = useCallback(async (ticker: string, name: string) => {
    if (!/^\d{6}$/.test(ticker)) return;

    const intentId = getGuestTrackIntentId(ticker);
    const ctaViewKey = `${ticker}|track-cta`;

    // 클릭이 발생했다면 CTA는 실제로 사용자에게 노출된 것이다.
    // IntersectionObserver 콜백보다 클릭이 먼저 발생하는 race를 보정해
    // track_click > track_cta_view 같은 불가능한 퍼널을 만들지 않는다.
    if (!guestConversionViewRef.current.has(ctaViewKey)) {
      guestConversionViewRef.current.add(ctaViewKey);
      trackUxEvent('track_cta_view', {
        ticker,
        stock_code: ticker,
        stock_name: name,
        source: 'main-guest-preview',
        placement: 'value',
        authenticated: false,
        exposure_mode: 'click_backfill',
        funnel_version: 'organic-v3',
        intent_id: intentId,
      });
    }

    saveStockTrackPending({
      type: 'WATCHLIST',
      ticker,
      name,
      source: 'main-guest-preview',
      intentId,
      createdAt: Date.now(),
    });
    const commonTrackPayload = {
      ticker,
      stock_code: ticker,
      stock_name: name,
      source: 'main-guest-preview',
      funnel_version: 'organic-v3',
      intent_id: intentId,
    };
    // 신규 click authority는 stock_track_click 하나만 기록한다.
    // main_guest_watchlist_add_click / track_stock_click은 historical read-only alias로 남긴다.
    trackUxEvent('stock_track_click', commonTrackPayload);

    // Guest 단계에서는 브라우저 알림 권한을 묻지 않는다.
    // 인증과 종목 자동 저장이 끝난 뒤 실제 추적 성공 상태에서 알림 opt-in을 제안한다.
    trackUxEvent('auth_start', {
      ...commonTrackPayload,
      source: 'main-guest-watchlist',
      intent: 'WATCHLIST',
      return_to: `/stocks/${ticker}`,
    });
    window.dispatchEvent(
      new CustomEvent('policy-intel:require-auth', {
        detail: {
          returnTo: `/stocks/${ticker}`,
          panelTab: 'SUMMARY',
          openPanel: false,
          source: 'main-guest-watchlist',
          intent: 'WATCHLIST',
          intentId,
          stockCode: ticker,
          stockName: name,
        },
      }),
    );
  }, [getGuestTrackIntentId]);

  const guestLeadStock = guestValuePreview?.stock ?? null;
  const guestLeadJudgment = guestValuePreview?.valueJudgment ?? null;
  const guestLeadSnapshot = guestValuePreview?.confirmedSnapshot ?? null;
  const guestLeadTicker = cleanText(guestLeadStock?.ticker ?? guestLeadStock?.stockCode ?? guestLeadStock?.stock_code);
  const guestLeadName = cleanText(guestLeadStock?.corpName ?? guestLeadStock?.corp_name ?? guestLeadStock?.name) || guestLeadTicker;
  const guestQuote = resolvePublicQuote(guestLeadStock ?? {}, guestLeadSnapshot);
  const guestLeadTradeDate = guestQuote.tradeDate;
  const guestLeadPrice = recentSession(guestQuote.tradeDate) ? guestQuote.price : null;








  const guestNewEvidenceRaw = guestLeadJudgment?.newEvidenceStatus ?? guestLeadJudgment?.newEvidence ?? {};

    const guestLeadReason = cleanText(guestLeadJudgment?.summary) || '핵심 재무·Event 근거를 확인 중입니다.';
    const guestLeadFundamental = cleanText(guestLeadJudgment?.fundamental?.label) || '기업가치 확인 필요';

      const guestLeadChecks = Array.isArray(guestLeadJudgment?.nextChecks) ? guestLeadJudgment.nextChecks.slice(0, 3) : [];
  const guestLeadChange = [
    ...(Array.isArray(guestLeadJudgment?.recentValueChanges) ? guestLeadJudgment.recentValueChanges : []),
    ...(Array.isArray(guestLeadJudgment?.recentEvidenceChanges) ? guestLeadJudgment.recentEvidenceChanges : []),
  ].map(cleanText).find(Boolean) || '';

  const guestLeadDisclosures = Array.isArray(guestLeadStock?.disclosures) ? guestLeadStock.disclosures : [];
  const guestLatestDisclosure = guestLeadDisclosures[0] ?? null;
  const guestLatestDisclosureTitle = cleanText(
    guestLatestDisclosure?.reportName
      ?? guestLatestDisclosure?.report_name
      ?? guestLatestDisclosure?.title,
  );
  const guestLatestDisclosureDate = cleanText(
    guestLatestDisclosure?.receiptDate
      ?? guestLatestDisclosure?.receipt_date
      ?? guestLatestDisclosure?.date,
  );
  const guestIssueDataStatus = guestLeadStock?.issueDataStatus ?? {};
  const guestIssueStatus = cleanText(guestIssueDataStatus?.status).toUpperCase();
  const guestIssueObserved90d = nullableNumber(guestIssueDataStatus?.observedCount90d);
  const guestIssuePending90d = nullableNumber(guestIssueDataStatus?.pendingCount90d);
  const guestIssueLatestObservedDate = cleanText(guestIssueDataStatus?.latestObservedDate);
    const guestIssueEmptyText = guestIssueStatus === 'PROCESSING'
    ? `최근 90일 공시 ${guestIssueObserved90d ?? 0}건 중 ${guestIssuePending90d ?? 0}건의 영향 분석이 아직 진행 중입니다.`
    : guestIssueStatus === 'NO_QUALIFYING_ISSUE'
      ? `최근 90일 공시 ${guestIssueObserved90d ?? 0}건을 확인했지만 현재 중요 변화로 분류된 항목은 없습니다.`
      : guestIssueStatus === 'NO_RECENT_DISCLOSURE_OBSERVED'
        ? `최근 90일 중요 공시 관측은 없습니다.${guestIssueLatestObservedDate ? ` 마지막 관측일 ${guestIssueLatestObservedDate}.` : ''} 수집 완료 여부와는 구분해 표시합니다.`
        : guestIssueStatus === 'UNAVAILABLE'
          ? '공시 수집 상태 authority를 조회하지 못해 최근 이슈 여부를 확정하지 않습니다.'
          : '종목별 수집 완료 authority가 없어 최근 이슈 없음으로 단정하지 않습니다.';
  const guestEvidenceChanges: string[] = Array.isArray(guestLeadJudgment?.recentEvidenceChanges)
    ? guestLeadJudgment.recentEvidenceChanges.map((value: unknown) => cleanText(value)).filter(Boolean)
    : [];
  const guestNewEvidenceSummary = cleanText(
    guestNewEvidenceRaw?.summary ?? guestLeadJudgment?.newEvidenceSummary,
  );
  const guestDisclosureImpactLabel = guestLatestDisclosureTitle
    ? guestEvidenceChanges[0]
      || guestNewEvidenceSummary
      || (guestIssueStatus === 'PROCESSING'
        ? '판단 영향 분석 중'
        : guestIssueStatus === 'NO_QUALIFYING_ISSUE'
          ? '현재 중요 변화로 분류되지 않음'
          : '현재 판단 영향 확인 중')
    : '';

    const guestDataQuality = guestLeadJudgment?.dataQuality ?? {};
  const guestFundamentalEvidence = guestLeadJudgment?.fundamentalEvidence ?? {};
  const guestFundamentalMetrics = guestFundamentalEvidence?.metrics ?? {};
  const guestMetricValue = (key: string): number | null => nullableNumber(guestFundamentalMetrics?.[key]);
  const guestFinancialMetricRows: Array<{ label: string; value: string }> = [
    { key: 'roicWaccSpreadPctp', label: 'ROIC-WACC', suffix: '%p', signed: true },
    { key: 'operatingMarginPct', label: '영업이익률', suffix: '%', signed: false },
    { key: 'revenueCagr3yPct', label: '3년 매출 성장률', suffix: '%', signed: true },
    { key: 'operatingIncomeGrowthYoyPct', label: '영업이익 성장률', suffix: '%', signed: true },
  ].map((metric) => {
    const value = guestMetricValue(metric.key);
    const prefix = value !== null && metric.signed && value > 0 ? '+' : '';
    return { label: metric.label, value: value === null ? '' : `${prefix}${value.toFixed(1)}${metric.suffix}` };
  }).filter((metric): metric is { label: string; value: string } => Boolean(metric.value)).slice(0, 3);
  const guestFinancialSnapshotMissing = guestFundamentalEvidence?.snapshotMissing === true
    || guestDataQuality?.fundamentalSnapshotMissing === true;
  const guestFinancialSnapshotStale = guestFundamentalEvidence?.snapshotStale === true
    || guestDataQuality?.fundamentalSnapshotStale === true;
  const guestFinancialReady = (guestFundamentalEvidence?.ready === true || guestDataQuality?.fundamentalReady === true)
    && Number(guestFundamentalEvidence?.metricCount ?? guestDataQuality?.fundamentalMetricCount ?? 0) > 0
    && !guestFinancialSnapshotMissing
    && !guestFinancialSnapshotStale;
  const guestFinancialBasisDate = cleanText(guestFundamentalEvidence?.basisDate ?? guestDataQuality?.fundamentalBasisDate);




  useEffect(() => {
    if (!guestValuePreview) return;
    const query = cleanText(guestStockQuery);
    const ticker = cleanText(
      guestValuePreview?.stock?.ticker
      ?? guestValuePreview?.stock?.stockCode
      ?? guestValuePreview?.stock?.stock_code,
    );
    const name = cleanText(
      guestValuePreview?.stock?.corpName
      ?? guestValuePreview?.stock?.corp_name
      ?? guestValuePreview?.stock?.name,
    );

    if (!query || (query !== ticker && query !== name)) {
      setGuestValuePreview(null);
      setGuestValuePreviewError('');
    }
  }, [guestStockQuery, guestValuePreview]);


  useEffect(() => {
    if (!guestLeadTicker || !guestPriceCardRef.current) return;
    const key = `${guestLeadTicker}|price|${guestLeadTradeDate}`;
    if (guestConversionViewRef.current.has(key)) return;
    const emit = () => {
      if (guestConversionViewRef.current.has(key)) return;
      guestConversionViewRef.current.add(key);
      trackUxEvent('price_view', {
        ticker: guestLeadTicker,
        stock_code: guestLeadTicker,
        source: 'main-guest-preview',
        price_available: guestLeadPrice !== null,
        price_source: guestQuote.source,
        older_observed_quote_skipped: guestQuote.ignoredOlderStock,
        trade_date: guestLeadTradeDate || null,
        exposure_mode: 'viewport_35pct',
        funnel_version: 'organic-v2',
      });
    };
    if (typeof IntersectionObserver === 'undefined') {
      emit();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.35)) {
        emit();
        observer.disconnect();
      }
    }, { threshold: [0.35] });
    observer.observe(guestPriceCardRef.current);
    return () => observer.disconnect();
  }, [guestLeadPrice, guestLeadTicker, guestLeadTradeDate, guestQuote.source, guestQuote.ignoredOlderStock]);

  useEffect(() => {
    if (!guestLeadTicker || !guestLeadJudgment) return;
    const observers: IntersectionObserver[] = [];
    const observeOnce = (
      node: Element | null,
      key: string,
      eventName: string,
      payload: Record<string, string | number | boolean | null | undefined>,
    ) => {
      if (!node || guestConversionViewRef.current.has(key)) return;
      const emit = () => {
        if (guestConversionViewRef.current.has(key)) return;
        guestConversionViewRef.current.add(key);
        trackUxEvent(eventName, payload);
      };
      if (typeof IntersectionObserver === 'undefined') {
        emit();
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.35)) {
          emit();
          observer.disconnect();
        }
      }, { threshold: [0.35] });
      observer.observe(node);
      observers.push(observer);
    };

    observeOnce(guestValueCardRef.current, `${guestLeadTicker}|value`, 'value_judgment_view', {
      ticker: guestLeadTicker,
      stock_code: guestLeadTicker,
      source: 'main-guest-preview',
      value_state: guestLeadJudgment?.valueState ?? null,
      exposure_mode: 'viewport_35pct',
      funnel_version: 'organic-v2',
    });
    observeOnce(guestReasonCardRef.current, `${guestLeadTicker}|reason`, 'judgment_reason_view', {
      ticker: guestLeadTicker,
      stock_code: guestLeadTicker,
      source: 'main-guest-preview',
      value_state: guestLeadJudgment?.valueState ?? null,
      exposure_mode: 'viewport_35pct',
      funnel_version: 'organic-v2',
    });
    if (guestLeadChecks.length > 0) {
      observeOnce(guestConditionCardRef.current, `${guestLeadTicker}|conditions`, 'judgment_condition_view', {
        ticker: guestLeadTicker,
        stock_code: guestLeadTicker,
        source: 'main-guest-preview',
        condition_count: guestLeadChecks.length,
        value_state: guestLeadJudgment?.valueState ?? null,
        exposure_mode: 'viewport_35pct',
        funnel_version: 'organic-v2',
      });
      observeOnce(guestConditionCardRef.current, `${guestLeadTicker}|change-conditions`, 'change_condition_view', {
        ticker: guestLeadTicker,
        stock_code: guestLeadTicker,
        source: 'main-guest-preview',
        condition_count: guestLeadChecks.length,
        value_state: guestLeadJudgment?.valueState ?? null,
        exposure_mode: 'viewport_35pct',
        funnel_version: 'organic-v2',
      });
    }
    observeOnce(guestTrackCtaButtonRef.current, `${guestLeadTicker}|track-cta`, 'track_cta_view', {
      ticker: guestLeadTicker,
      stock_code: guestLeadTicker,
      stock_name: guestLeadName,
      source: 'main-guest-preview',
      placement: 'value',
      authenticated: false,
      exposure_mode: 'viewport_35pct',
      funnel_version: 'organic-v3',
      intent_id: getGuestTrackIntentId(guestLeadTicker),
      value_state: guestLeadJudgment?.valueState ?? null,
    });
    return () => observers.forEach((observer) => observer.disconnect());
  }, [guestLeadChecks.length, guestLeadJudgment, guestLeadName, guestLeadTicker, getGuestTrackIntentId]);


  // One V8 entry for guests and members; no hidden legacy main.

    const changeSource = homeChanges?.changes;
    const changeRows: any[] = Array.isArray(changeSource)
      ? [...changeSource].sort((a: any, b: any) => homeChangeLayerPriority(a) - homeChangeLayerPriority(b)).slice(0, 3)
      : [];
    return (
      <>
        <SEO
          title="시장이 왜 움직였나 | 마지막 거래일·주말 브리핑·종목 분석 | PolicyIntel"
          description="지수와 실제 상승 종목의 차이, 외국인 수급과 업종 움직임, 다음 거래일 검증 조건을 확인하세요. 종목 분석은 로그인 없이 시작할 수 있습니다."
          path="/"
        />
        <MarketWorkspaceV8
          authenticated={isAuthenticated}
          summary={liveSummary as any}
          dashboard={authDashboard as any}
          changes={isAuthenticated ? changeRows : []}
          loading={loading}
          query={guestStockQuery}
          onQuery={handleGuestQueryChange}
          suggestions={guestStockSuggestions}
          onSelect={openGuestStockPreview}
          onSubmit={submitGuestStockSearch}
          preview={guestValuePreview}
          previewLoading={guestValuePreviewLoading}
          previewError={guestValuePreviewError}
          stockResearch={{
            summary: guestLeadReason,
            change: guestLeadChange,
            checks: guestLeadChecks.map(cleanText).filter(Boolean),
            fundamental: guestLeadFundamental,
            financialReady: guestFinancialReady,
            financialBasisDate: guestFinancialBasisDate,
            financialMetrics: guestFinancialMetricRows,
            disclosureTitle: guestLatestDisclosureTitle,
            disclosureDate: guestLatestDisclosureDate,
            disclosureImpact: guestDisclosureImpactLabel,
            disclosureEmptyText: guestIssueEmptyText,
          }}
          exposureRefs={{ price: guestPriceCardRef, value: guestValueCardRef, reason: guestReasonCardRef,
            condition: guestConditionCardRef, trackCta: guestTrackCtaButtonRef }}
          onWatch={requireLoginForWatchlist}
          onEvidence={requireLoginForGuestEvidence}
          onOpen={(tab, source, target) => openActionDesk(tab as LiveSummaryTab, source, target)}
          onNavigate={navigate}
        />
        <UserFeedbackLauncher source="MAIN" variant="floating" />
      </>
    );
}
